{
  "metadata": {
    "formats": {
      "WEB_URL": {
        "description": "A URL that starts with `http://` or `https://`.",
        "validation": {
          "type": "web_url"
        }
      },
      "COLOR_HEX": {
        "description": "A color in hexadecimal RGB or RGBA format, for example `#FF0000` or `#FF000050`. `COLOR_HEX` accepts shorthand, for example #F00 or #F005.",
        "validation": {
          "type": "regex",
          "regex": "^#(?:[0-9A-Fa-f]{3,4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$"
        }
      },
      "NODE_ID": {
        "description": "See [Node ID](#node-ids).",
        "validation": {
          "type": "regex",
          "regex": "^[-_\\w]*$"
        }
      }
    }
  },
  "properties": {
    "nodes": {
      "metadata": {
        "description": "Array of nodes representing content elements, such as `ParagraphNode` or `HeadingNode`."
      },
      "elements": {
        "ref": "RootNode"
      }
    }
  },
  "optionalProperties": {
    "metadata": {
      "ref": "Metadata",
      "metadata": {
        "description": "Information about the Ricos document itself, such as its version. Only relevant when building the Ricos document using the [Ricos playground](https:/ricos.dev), in which case it is auto-generated."
      }
    },
    "documentStyle": {
      "ref": "DocumentStyle",
      "metadata": {
        "description": "Includes styling to be applied throughout the Ricos document for the specified node types. For example, you can define general styling for `headerOne`, and then override some of the properties separately for a specific header."
      }
    }
  },
  "definitions": {
    "NodeStyle": {
      "metadata": {
        "features": [
          "LINE_SPACING"
        ]
      },
      "optionalProperties": {
        "paddingTop": {
          "metadata": {
            "description": "The top padding value in pixels."
          },
          "type": "string"
        },
        "paddingBottom": {
          "metadata": {
            "description": "The bottom padding value in pixels."
          },
          "type": "string"
        },
        "backgroundColor": {
          "metadata": {
            "deprecated": true
          },
          "type": "string"
        }
      }
    },
    "DocumentStyle": {
      "metadata": {
        "description": "Sets a default styling for specific node types throughout the entire Ricos Document. For example, you can set the text color for all Heading 1 nodes.\nYou can override the styling of specific Text nodes separately using [decorations](#decorations).",
        "examples": [
          "document-style"
        ],
        "section": "Document Styles"
      },
      "optionalProperties": {
        "headerOne": {
          "metadata": {
            "description": "Style for the H1 nodes."
          },
          "ref": "TextNodeStyle"
        },
        "headerTwo": {
          "metadata": {
            "description": "Style for the H2 nodes."
          },
          "ref": "TextNodeStyle"
        },
        "headerThree": {
          "metadata": {
            "description": "Style for the H3 nodes."
          },
          "ref": "TextNodeStyle"
        },
        "headerFour": {
          "metadata": {
            "description": "Style for the H4 nodes."
          },
          "ref": "TextNodeStyle"
        },
        "headerFive": {
          "metadata": {
            "description": "Style for the H5 nodes."
          },
          "ref": "TextNodeStyle"
        },
        "headerSix": {
          "metadata": {
            "description": "Style for the H6 nodes."
          },
          "ref": "TextNodeStyle"
        },
        "paragraph": {
          "metadata": {
            "description": "Style for the paragraph nodes."
          },
          "ref": "TextNodeStyle"
        },
        "blockquote": {
          "metadata": {
            "description": "Style for the block quote nodes."
          },
          "ref": "TextNodeStyle"
        },
        "codeBlock": {
          "metadata": {
            "description": "Style for the codeblock nodes."
          },
          "ref": "TextNodeStyle"
        }
      }
    },
    "TextNodeStyle": {
      "metadata": {
        "section": "Document Styles"
      },
      "optionalProperties": {
        "decorations": {
          "elements": {
            "ref": "Decoration"
          },
          "metadata": {
            "description": "Text node decorations."
          }
        },
        "nodeStyle": {
          "ref": "NodeStyle",
          "metadata": {
            "description": "Node style, such as padding. \n **Note:** This node requires the [lineSpacing](#plugins) plugin to be enabled."
          }
        },
        "lineHeight": {
          "type": "string",
          "metadata": {
            "description": "Text line height."
          }
        }
      }
    },
    "Metadata": {
      "metadata": {
        "section": "Metadata"
      },
      "optionalProperties": {
        "version": {
          "type": "int32",
          "metadata": {
            "description": "Ricos document version."
          }
        },
        "id": {
          "metadata": {
            "deprecated": true
          }
        },
        "createdTimestamp": {
          "metadata": {
            "deprecated": true
          }
        },
        "updatedTimestamp": {
          "metadata": {
            "deprecated": true
          }
        }
      }
    },
    "TextStyle": {
      "optionalProperties": {
        "textAlignment": {
          "metadata": {
            "emitEnum": "TextStyle_TextAlignment",
            "description": "Text alignment. Supported values: <br> - `AUTO`: Text aligns itself automatically. <br> - `LEFT`: Text is aligned to the left. <br> - `RIGHT`: Text is aligned to the right. <br> - `CENTER`: Text is centered. <br> - `JUSTIFY`: Text is justified to both margins. <br> Default: `AUTO`."
          },
          "enum": [
            "AUTO",
            "LEFT",
            "RIGHT",
            "CENTER",
            "JUSTIFY"
          ]
        },
        "lineHeight": {
          "metadata": {
            "description": "Vertical spacing between lines of text in an element."
          },
          "type": "string"
        }
      },
      "metadata": {
        "section": "Common objects"
      }
    },
    "Link": {
      "metadata": {
        "section": "Common objects",
        "description": "Link object.",
        "oneOf": {
          "strict": true,
          "properties": [
            "url",
            "anchor"
          ]
        }
      },
      "optionalProperties": {
        "url": {
          "metadata": {
            "description": "Absolute URL to link to."
          },
          "type": "string"
        },
        "anchor": {
          "metadata": {
            "description": "Target [Node ID](#node-ids) to link to."
          },
          "type": "string"
        },
        "target": {
          "metadata": {
            "emitEnum": "Link_Target",
            "description": "The HTML `target` attribute value for the link. This property defines where the link opens. <br> Supported values: <br> `SELF` - Opens the link in the same frame.<br> `BLANK` - Opens the link in a new browser tab or window.<br> `PARENT` - Opens the link in the parent frame.<br> `TOP` - Opens the link in the full body of the link's browser tab or window. <br> Default: `SELF`"
          },
          "enum": [
            "SELF",
            "BLANK",
            "PARENT",
            "TOP"
          ]
        },
        "rel": {
          "metadata": {
            "description": "Relationship between the current document and the linked document."
          },
          "ref": "Link_Rel"
        },
        "customData": {
          "type": "string",
          "metadata": {
            "deprecated": true,
            "internal": true,
            "description": "A serialized object used for a custom or external link panel."
          }
        }
      }
    },
    "Link_Rel": {
      "optionalProperties": {
        "nofollow": {
          "metadata": {
            "description": "Indicates to search engine crawlers not to follow the link. \n Default: `false`"
          },
          "type": "boolean"
        },
        "sponsored": {
          "metadata": {
            "description": "Indicates to search engine crawlers that the link is a paid placement such as sponsored content or an advertisement. \n Default: `false`"
          },
          "type": "boolean"
        },
        "ugc": {
          "metadata": {
            "description": "Indicates that this link is user-generated content and isn't necessarily trusted or endorsed by the page author. \n Default: `false`"
          },
          "type": "boolean"
        },
        "noreferrer": {
          "metadata": {
            "description": "Indicates that this link protects referral information from being passed to the target website."
          },
          "type": "boolean"
        }
      },
      "metadata": {
        "section": "Common objects"
      }
    },
    "FileSource": {
      "metadata": {
        "section": "Common objects",
        "description": "File source object.",
        "oneOf": {
          "strict": true,
          "properties": [
            "url",
            "id"
          ]
        }
      },
      "optionalProperties": {
        "url": {
          "metadata": {
            "description": "File source URL."
          },
          "type": "string"
        },
        "id": {
          "metadata": {
            "description": "ID that's resolved to a URL by a resolver function."
          },
          "type": "string"
        },
        "private": {
          "metadata": {
            "description": "Whether the file's source is private. Learn more about private files in Wix ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/files/private-files) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/files/private-files)). \n Default: `false`"
          },
          "type": "boolean"
        }
      }
    },
    "Media": {
      "properties": {
        "src": {
          "metadata": {
            "description": "File source."
          },
          "ref": "FileSource"
        }
      },
      "optionalProperties": {
        "width": {
          "metadata": {
            "description": "Media width in pixels."
          },
          "type": "int32"
        },
        "height": {
          "metadata": {
            "description": "Media height in pixels."
          },
          "type": "int32"
        },
        "duration": {
          "metadata": {
            "description": "Media duration in seconds. Only relevant for audio and video files."
          },
          "type": "float64"
        }
      },
      "metadata": {
        "section": "Common objects"
      }
    },
    "Never": {
      "metadata": {
        "section": "Common objects",
        "internal": true
      },
      "properties": {
        "_neverCreatedElement": {}
      }
    },
    "PluginContainerData": {
      "optionalProperties": {
        "width": {
          "metadata": {
            "description": "Node display width."
          },
          "ref": "PluginContainerData_Width"
        },
        "alignment": {
          "metadata": {
            "emitEnum": "PluginContainerData_Alignment",
            "description": "Node alignment in its container."
          },
          "enum": [
            "CENTER",
            "LEFT",
            "RIGHT"
          ]
        },
        "spoiler": {
          "metadata": {
            "description": "Spoiler cover settings.",
            "features": [
              "SPOILER"
            ]
          },
          "ref": "PluginContainerData_Spoiler"
        },
        "height": {
          "metadata": {
            "description": "Node display height."
          },
          "ref": "PluginContainerData_Height"
        },
        "textWrap": {
          "metadata": {
            "description": "Whether the text should wrap around the node when it's displayed. If `textWrap` is `false`, the node fills the width of its container. \n Default: `true` for all node types except `DIVIDER`."
          },
          "type": "boolean"
        }
      },
      "metadata": {
        "section": "containerdata"
      }
    },
    "PluginContainerData_Spoiler": {
      "optionalProperties": {
        "enabled": {
          "metadata": {
            "description": "Whether the spoiler cover is enabled. \n Default: `false`"
          },
          "type": "boolean"
        },
        "description": {
          "metadata": {
            "description": "Text displayed on the spoiler cover."
          },
          "type": "string"
        },
        "buttonText": {
          "metadata": {
            "description": "Text that appears on the button that removes the spoiler cover."
          },
          "type": "string"
        }
      },
      "metadata": {
        "section": "containerdata"
      }
    },
    "PluginContainerData_Width": {
      "metadata": {
        "section": "containerdata",
        "description": "Node display width.",
        "oneOf": {
          "properties": [
            "size",
            "custom"
          ]
        }
      },
      "optionalProperties": {
        "size": {
          "metadata": {
            "emitEnum": "PluginContainerData_Width_Type",
            "description": "Width of a node in a container. Supported values: \n - `CONTENT`: Width of the container matches the content width. \n - `SMALL`: Small width. \n - `ORIGINAL`: Width of the container matches the original image width. **Note:** This value is only applicable for `imageData` containers. \n `FULL_WIDTH`: Image container takes up the full width of the screen. **Note:** This value is only applicable for `imageData` containers."
          },
          "enum": [
            "CONTENT",
            "SMALL",
            "ORIGINAL",
            "FULL_WIDTH"
          ]
        },
        "custom": {
          "metadata": {
            "description": "A custom width value in pixels."
          },
          "type": "string"
        }
      }
    },
    "PluginContainerData_Height": {
      "optionalProperties": {
        "custom": {
          "metadata": {
            "description": "Node display height in pixels."
          },
          "type": "string"
        }
      },
      "metadata": {
        "section": "containerdata"
      }
    },
    "Decoration": {
      "discriminator": "type",
      "metadata": {
        "section": "Decorations",
        "refUnion": {
          "BOLD": "BoldDecoration",
          "ITALIC": "ItalicDecoration",
          "UNDERLINE": "UnderlineDecoration",
          "SPOILER": "SpoilerDecoration",
          "ANCHOR": "AnchorDecoration",
          "MENTION": "MentionDecoration",
          "LINK": "LinkDecoration",
          "COLOR": "ColorDecoration",
          "FONT_SIZE": "FontSizeDecoration"
        },
        "emitEnum": "Decoration_Type"
      },
      "mapping": {
        "BOLD": {
          "metadata": {
            "section": "Bold",
            "examples": [
              "bold"
            ],
            "description": "Bolds text, which makes it appear thicker and more prominent than normal text."
          },
          "properties": {},
          "optionalProperties": {
            "fontWeightValue": {
              "type": "float32",
              "metadata": {
                "description": "Font weight."
              }
            }
          }
        },
        "ITALIC": {
          "metadata": {
            "section": "Italic",
            "examples": [
              "italic"
            ],
            "description": "Italicizes text, which makes it appear slanted or italicized. Italics are typically used for emphasis, citations, or to distinguish certain types of content."
          },
          "properties": {},
          "optionalProperties": {
            "italicData": {
              "type": "boolean",
              "metadata": {
                "description": "Whether to enable the italic text."
              }
            }
          }
        },
        "UNDERLINE": {
          "metadata": {
            "section": "Underline",
            "examples": [
              "underline"
            ],
            "description": "Underlines text."
          },
          "properties": {},
          "optionalProperties": {
            "underlineData": {
              "type": "boolean",
              "metadata": {
                "description": "Whether to underline the text."
              }
            }
          }
        },
        "SPOILER": {
          "metadata": {
            "section": "Spoiler",
            "examples": [
              "spoiler"
            ],
            "features": [
              "SPOILER"
            ],
            "description": "Blurs text. <br> **Note:** This decoration requires the [`spoiler`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
          },
          "properties": {},
          "optionalProperties": {
            "spoilerData": {
              "ref": "SpoilerData",
              "metadata": {
                "description": "Spoiler details."
              }
            }
          }
        },
        "ANCHOR": {
          "metadata": {
            "section": "Anchor",
            "examples": [
              "anchor"
            ],
            "description": "Makes clickable text a different color. The link navigates to another section of the same page."
          },
          "properties": {},
          "optionalProperties": {
            "anchorData": {
              "ref": "AnchorData",
              "metadata": {
                "description": "Anchor details."
              }
            }
          }
        },
        "MENTION": {
          "metadata": {
            "section": "Mention",
            "examples": [
              "mention"
            ],
            "features": [
              "MENTION"
            ],
            "description": "Text that mentions a specific user, often in social media platforms, forums, or content management systems. <br> **Note:** This decoration requires the [`mention`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
          },
          "properties": {},
          "optionalProperties": {
            "mentionData": {
              "ref": "MentionData",
              "metadata": {
                "description": "Anchor details."
              }
            }
          }
        },
        "LINK": {
          "metadata": {
            "section": "Link Decoration",
            "examples": [
              "link"
            ],
            "features": [
              "LINK"
            ],
            "description": "Makes clickable text a different color. The link navigates to another webpage or resource. <br> **Note:** This decoration requires the [`link`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
          },
          "properties": {},
          "optionalProperties": {
            "linkData": {
              "ref": "LinkData",
              "metadata": {
                "description": "Link details."
              }
            }
          }
        },
        "COLOR": {
          "metadata": {
            "section": "Color",
            "examples": [
              "color"
            ],
            "features": [
              "COLOR_DECORATION"
            ],
            "description": "Styles text using background and text colors. <br> **Note:** This decoration requires the [`colorDecoration`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
          },
          "properties": {},
          "optionalProperties": {
            "colorData": {
              "ref": "ColorData",
              "metadata": {
                "description": "Color details."
              }
            }
          }
        },
        "FONT_SIZE": {
          "metadata": {
            "section": "FontSize",
            "examples": [
              "font-size"
            ],
            "description": "Text font size."
          },
          "properties": {},
          "optionalProperties": {
            "fontSizeData": {
              "ref": "FontSizeData",
              "metadata": {
                "description": "Font size details."
              }
            }
          }
        }
      }
    },
    "BoldDecoration": {
      "metadata": {
        "section": "Bold",
        "examples": [
          "bold"
        ],
        "description": "Bolds text, which makes it appear thicker and more prominent than normal text."
      },
      "properties": {
        "type": {
          "enum": [
            "BOLD"
          ],
          "metadata": {
            "description": "Decoration type. \n Must be `BOLD`."
          }
        }
      },
      "optionalProperties": {
        "fontWeightValue": {
          "type": "float32",
          "metadata": {
            "description": "Font weight."
          }
        }
      }
    },
    "ItalicDecoration": {
      "metadata": {
        "section": "Italic",
        "examples": [
          "italic"
        ],
        "description": "Italicizes text, which makes it appear slanted or italicized. Italics are typically used for emphasis, citations, or to distinguish certain types of content."
      },
      "properties": {
        "type": {
          "enum": [
            "ITALIC"
          ],
          "metadata": {
            "description": "Decoration type. \n Must be `ITALIC`."
          }
        }
      },
      "optionalProperties": {
        "italicData": {
          "type": "boolean",
          "metadata": {
            "description": "Whether to enable the italic text."
          }
        }
      }
    },
    "UnderlineDecoration": {
      "metadata": {
        "section": "Underline",
        "examples": [
          "underline"
        ],
        "description": "Underlines text."
      },
      "properties": {
        "type": {
          "enum": [
            "UNDERLINE"
          ],
          "metadata": {
            "description": "Decoration type. \nMust be `UNDERLINE`."
          }
        }
      },
      "optionalProperties": {
        "underlineData": {
          "type": "boolean",
          "metadata": {
            "description": "Whether to underline the text."
          }
        }
      }
    },
    "SpoilerDecoration": {
      "metadata": {
        "section": "Spoiler",
        "examples": [
          "spoiler"
        ],
        "features": [
          "SPOILER"
        ],
        "description": "Blurs text. <br> **Note:** This decoration requires the [`spoiler`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
      },
      "properties": {
        "type": {
          "enum": [
            "SPOILER"
          ],
          "metadata": {
            "description": "Decoration type. \n Must be `SPOILER`."
          }
        }
      },
      "optionalProperties": {
        "spoilerData": {
          "ref": "SpoilerData",
          "metadata": {
            "description": "Spoiler details."
          }
        }
      }
    },
    "AnchorDecoration": {
      "metadata": {
        "section": "Anchor",
        "examples": [
          "anchor"
        ],
        "description": "Makes clickable text a different color. The link navigates to another section of the same page."
      },
      "properties": {
        "type": {
          "enum": [
            "ANCHOR"
          ],
          "metadata": {
            "description": "Decoration type. \n Must be `ANCHOR`."
          }
        }
      },
      "optionalProperties": {
        "anchorData": {
          "ref": "AnchorData",
          "metadata": {
            "description": "Anchor details."
          }
        }
      }
    },
    "MentionDecoration": {
      "metadata": {
        "section": "Mention",
        "examples": [
          "mention"
        ],
        "features": [
          "MENTION"
        ],
        "description": "Text that mentions a specific user, often in social media platforms, forums, or content management systems. <br> **Note:** This decoration requires the [`mention`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
      },
      "properties": {
        "type": {
          "enum": [
            "MENTION"
          ],
          "metadata": {
            "description": "Decoration type. \n Must be `MENTION`."
          }
        }
      },
      "optionalProperties": {
        "mentionData": {
          "ref": "MentionData",
          "metadata": {
            "description": "Anchor details."
          }
        }
      }
    },
    "LinkDecoration": {
      "metadata": {
        "section": "Link Decoration",
        "examples": [
          "link"
        ],
        "features": [
          "LINK"
        ],
        "description": "Makes clickable text a different color. The link navigates to another webpage or resource. <br> **Note:** This decoration requires the [`link`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
      },
      "properties": {
        "type": {
          "enum": [
            "LINK"
          ],
          "metadata": {
            "description": "Decoration type. \n Must be `LINK`."
          }
        }
      },
      "optionalProperties": {
        "linkData": {
          "ref": "LinkData",
          "metadata": {
            "description": "Link details."
          }
        }
      }
    },
    "ColorDecoration": {
      "metadata": {
        "section": "Color",
        "examples": [
          "color"
        ],
        "features": [
          "COLOR_DECORATION"
        ],
        "description": "Styles text using background and text colors. <br> **Note:** This decoration requires the [`colorDecoration`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
      },
      "properties": {
        "type": {
          "enum": [
            "COLOR"
          ],
          "metadata": {
            "description": "Decoration type. \n Value: `COLOR`"
          }
        }
      },
      "optionalProperties": {
        "colorData": {
          "ref": "ColorData",
          "metadata": {
            "description": "Color details."
          }
        }
      }
    },
    "FontSizeDecoration": {
      "metadata": {
        "section": "FontSize",
        "examples": [
          "font-size"
        ],
        "description": "Text font size."
      },
      "properties": {
        "type": {
          "enum": [
            "FONT_SIZE"
          ],
          "metadata": {
            "description": "Decoration type. \n Must be `FONT_SIZE`."
          }
        }
      },
      "optionalProperties": {
        "fontSizeData": {
          "ref": "FontSizeData",
          "metadata": {
            "description": "Font size details."
          }
        }
      }
    },
    "AnchorData": {
      "metadata": {
        "section": "Anchor"
      },
      "optionalProperties": {
        "anchor": {
          "metadata": {
            "description": "Target Node ID to redirect to."
          },
          "type": "string"
        }
      }
    },
    "ColorData": {
      "metadata": {
        "section": "Color"
      },
      "optionalProperties": {
        "background": {
          "metadata": {
            "description": "Background color as a hexadecimal value."
          },
          "type": "string"
        },
        "foreground": {
          "metadata": {
            "description": "Text color as a hexadecimal value."
          },
          "type": "string"
        }
      }
    },
    "LinkData": {
      "metadata": {
        "section": "Link Decoration"
      },
      "optionalProperties": {
        "link": {
          "ref": "Link",
          "metadata": {
            "description": "Link object."
          }
        }
      }
    },
    "MentionData": {
      "metadata": {
        "section": "Mention"
      },
      "optionalProperties": {
        "name": {
          "type": "string",
          "metadata": {
            "description": "Display name of the mentioned user."
          }
        },
        "slug": {
          "type": "string",
          "metadata": {
            "description": "Username of the mentioned user. This is th text that appears after the \"@\"."
          }
        },
        "id": {
          "type": "string",
          "metadata": {
            "description": "ID of the mentioned user."
          }
        }
      }
    },
    "FontSizeData": {
      "metadata": {
        "section": "FontSize"
      },
      "properties": {
        "value": {
          "type": "float32",
          "metadata": {
            "description": "Font size value."
          }
        }
      },
      "optionalProperties": {
        "unit": {
          "metadata": {
            "description": "Font size units. \n Default: `PX`",
            "emitEnum": "FontSizeData_fontType"
          },
          "enum": [
            "PX",
            "EM"
          ]
        }
      }
    },
    "SpoilerData": {
      "metadata": {
        "section": "Spoiler"
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        }
      }
    },
    "BlockquoteNode": {
      "metadata": {
        "section": "Block quote",
        "examples": [
          "blockquote"
        ],
        "description": "Used to visually distinguish quoted or cited content from the surrounding text. When rendered, it displays as the quoted text with indentation or other styling to visually separate it from the surrounding content."
      },
      "properties": {
        "type": {
          "enum": [
            "BLOCKQUOTE"
          ],
          "metadata": {
            "description": "Node type. \n Must be `BLOCKQUOTE`."
          }
        },
        "nodes": {
          "elements": {
            "ref": "ParagraphNode"
          },
          "metadata": {
            "description": "Child nodes to define a blockquote paragraph.",
            "validation": {
              "minSize": 1,
              "maxSize": 1
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "blockquoteData": {
          "ref": "BlockquoteData",
          "metadata": {
            "description": "Blockquote details."
          }
        },
        "style": {
          "ref": "NodeStyle",
          "metadata": {
            "description": "Defines the blockquote style, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
          }
        }
      }
    },
    "BlockquoteData": {
      "optionalProperties": {
        "indentation": {
          "type": "int32",
          "metadata": {
            "description": "Blockquote margin from the left side. \n The indentation is measured relative to the font size *em* units by a ratio of 1.5. For example, if the indentation is `2`, then the margin is equal to **3em**. Therefore the margin is 3 times the size of the font."
          }
        }
      },
      "metadata": {
        "section": "Block quote"
      }
    },
    "CaptionNode": {
      "metadata": {
        "section": "Caption",
        "examples": [],
        "description": "Caption container to be attached to media elements, such as images. Captions support formatted text content and can be styled independently from their parent nodes."
      },
      "properties": {
        "type": {
          "enum": [
            "CAPTION"
          ],
          "metadata": {
            "description": "Node type. \n Must be `CAPTION`."
          }
        },
        "nodes": {
          "elements": {
            "ref": "TextNode"
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "description": "Caption [node ID](#node-ids)."
          }
        },
        "style": {
          "ref": "NodeStyle",
          "metadata": {
            "description": "Defines the caption styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
          }
        },
        "captionData": {
          "ref": "CaptionData",
          "metadata": {
            "description": "Caption options."
          }
        }
      }
    },
    "CaptionData": {
      "optionalProperties": {
        "textStyle": {
          "ref": "TextStyle",
          "metadata": {
            "description": "Caption text styling."
          }
        }
      },
      "metadata": {
        "section": "Caption"
      }
    },
    "CodeBlockNode": {
      "metadata": {
        "section": "Codeblock",
        "features": [
          "CODE_BLOCK"
        ],
        "examples": [
          "code-block-multi-line",
          "code-block-syntax-highlighting"
        ],
        "description": "Presents code snippets, markup, or other text-based content that should be displayed exactly as it appears, without any interpretation or formatting applied by the browser. When rendered, it is displayed as the code block with a monospaced font, preserving indentation, line breaks, and spacing as specified in the code."
      },
      "properties": {
        "type": {
          "enum": [
            "CODE_BLOCK"
          ],
          "metadata": {
            "description": "Node type. \n VMust be `CODE_BLOCK`."
          }
        }
      },
      "optionalProperties": {
        "nodes": {
          "elements": {
            "ref": "TextNode"
          },
          "metadata": {
            "description": "Child nodes to define a code block text."
          }
        },
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "codeBlockData": {
          "ref": "CodeBlockData",
          "metadata": {
            "description": "Code block details."
          }
        },
        "style": {
          "ref": "NodeStyle",
          "metadata": {
            "description": "Code block styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
          }
        }
      }
    },
    "CodeBlockData": {
      "optionalProperties": {
        "textStyle": {
          "ref": "TextStyle",
          "metadata": {
            "description": "Defines the text styling, such as alignment and line height."
          }
        }
      },
      "metadata": {
        "section": "Codeblock"
      }
    },
    "HeadingNode": {
      "metadata": {
        "section": "Heading",
        "examples": [
          "headings-all-sizes"
        ],
        "description": "Indicates a heading or title of a section. There are 6 levels of headings, with `1` being the most prominent and `6` being the least."
      },
      "properties": {
        "type": {
          "enum": [
            "HEADING"
          ],
          "metadata": {
            "description": "Node type. \n Must be `HEADING`."
          }
        }
      },
      "optionalProperties": {
        "nodes": {
          "elements": {
            "ref": "TextNode"
          },
          "metadata": {
            "description": "Child nodes to define the heading text."
          }
        },
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "style": {
          "ref": "NodeStyle",
          "metadata": {
            "description": "Heading styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
          }
        },
        "headingData": {
          "ref": "HeadingData",
          "metadata": {
            "description": "Heading options."
          }
        }
      }
    },
    "HeadingData": {
      "optionalProperties": {
        "level": {
          "type": "int32",
          "metadata": {
            "description": "Heading size. <br> Mi: `1` <br> Max: `6`",
            "validation": {
              "min": 1,
              "max": 6
            }
          }
        },
        "textStyle": {
          "ref": "TextStyle",
          "metadata": {
            "description": "Heading text styling, such as text alignment and line height."
          }
        },
        "indentation": {
          "type": "int32",
          "metadata": {
            "description": "Heading margin from the left side. \n The indentation is measured relative to the font size *em* units by a ratio of 1.5. For example, if the indentation is `2`, then the margin is equal to **3em**. Therefore the margin is 3 times the size of the font."
          }
        }
      },
      "metadata": {
        "section": "Heading"
      }
    },
    "LayoutNode": {
      "metadata": {
        "section": "Layout",
        "examples": [
          "layout"
        ],
        "description": "Used to wrap cells in layout container. When rendered, it cells one after the other in a row"
      },
      "properties": {
        "type": {
          "enum": [
            "LAYOUT"
          ],
          "metadata": {
            "description": "Node type. \n Must be `LAYOUT`."
          }
        },
        "nodes": {
          "elements": {
            "ref": "LayoutCellNode"
          },
          "metadata": {
            "description": "Cell items.",
            "validation": {
              "minSize": 1,
              "maxSize": 3
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "layoutData": {
          "ref": "LayoutData",
          "metadata": {
            "description": "Layout details."
          }
        }
      }
    },
    "LayoutData": {
      "optionalProperties": {},
      "metadata": {
        "section": "Layout"
      }
    },
    "LayoutCellNode": {
      "metadata": {
        "section": "Layout",
        "description": "Used to wrap content inside layout container. When rendered, it shows content inside layout column"
      },
      "properties": {
        "type": {
          "enum": [
            "LAYOUT_CELL"
          ],
          "metadata": {
            "description": "Node type. \n Must be `LAYOUT_CELL`."
          }
        },
        "nodes": {
          "elements": {
            "ref": "LayoutCellChildNode"
          },
          "metadata": {
            "description": "Child nodes that the cell item contains."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "layoutCellData": {
          "ref": "LayoutCellData",
          "metadata": {
            "description": "Layout Cell details."
          }
        }
      }
    },
    "LayoutCellData": {
      "optionalProperties": {
        "colSpan": {
          "type": "int32",
          "metadata": {
            "description": "Defines how many columns cell spans inside 12 column grid. If not provided it will divide remaining space equally.",
            "validation": {
              "min": 3,
              "max": 12
            }
          }
        }
      },
      "metadata": {
        "section": "Layout"
      }
    },
    "LayoutCellChildNode": {
      "discriminator": "type",
      "metadata": {
        "section": "Layout",
        "refUnion": {
          "BLOCKQUOTE": "BlockquoteNode",
          "CODE_BLOCK": "CodeBlockNode",
          "HEADING": "HeadingNode",
          "ORDERED_LIST": "OrderedListNode",
          "BULLETED_LIST": "BulletedListNode",
          "PARAGRAPH": "ParagraphNode",
          "APP_EMBED": "AppEmbedNode",
          "AUDIO": "AudioNode",
          "BUTTON": "ButtonNode",
          "COLLAPSIBLE_LIST": "CollapsibleListNode",
          "DIVIDER": "DividerNode",
          "EMBED": "EmbedNode",
          "FILE": "FileNode",
          "GALLERY": "GalleryNode",
          "GIF": "GifNode",
          "HTML": "HtmlNode",
          "IMAGE": "ImageNode",
          "LINK_PREVIEW": "LinkPreviewNode",
          "POLL": "PollNode",
          "VIDEO": "VideoNode"
        }
      },
      "mapping": {
        "BLOCKQUOTE": {
          "metadata": {
            "section": "Block quote",
            "examples": [
              "blockquote"
            ],
            "description": "Used to visually distinguish quoted or cited content from the surrounding text. When rendered, it displays as the quoted text with indentation or other styling to visually separate it from the surrounding content."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ParagraphNode"
              },
              "metadata": {
                "description": "Child nodes to define a blockquote paragraph.",
                "validation": {
                  "minSize": 1,
                  "maxSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "blockquoteData": {
              "ref": "BlockquoteData",
              "metadata": {
                "description": "Blockquote details."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Defines the blockquote style, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            }
          }
        },
        "CODE_BLOCK": {
          "metadata": {
            "section": "Codeblock",
            "features": [
              "CODE_BLOCK"
            ],
            "examples": [
              "code-block-multi-line",
              "code-block-syntax-highlighting"
            ],
            "description": "Presents code snippets, markup, or other text-based content that should be displayed exactly as it appears, without any interpretation or formatting applied by the browser. When rendered, it is displayed as the code block with a monospaced font, preserving indentation, line breaks, and spacing as specified in the code."
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Child nodes to define a code block text."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "codeBlockData": {
              "ref": "CodeBlockData",
              "metadata": {
                "description": "Code block details."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Code block styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            }
          }
        },
        "HEADING": {
          "metadata": {
            "section": "Heading",
            "examples": [
              "headings-all-sizes"
            ],
            "description": "Indicates a heading or title of a section. There are 6 levels of headings, with `1` being the most prominent and `6` being the least."
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Child nodes to define the heading text."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Heading styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "headingData": {
              "ref": "HeadingData",
              "metadata": {
                "description": "Heading options."
              }
            }
          }
        },
        "ORDERED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "orderedList"
            ],
            "description": "Numbered list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "orderedListData": {
              "ref": "OrderedListData",
              "metadata": {
                "description": "Ordered list details."
              }
            }
          }
        },
        "BULLETED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "bulletedList"
            ],
            "description": "Bulleted list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "bulletedListData": {
              "ref": "BulletedListData",
              "metadata": {
                "description": "Bulleted list details."
              }
            }
          }
        },
        "PARAGRAPH": {
          "metadata": {
            "section": "Paragraph",
            "description": "Structures text content, providing a clear separation between different ideas or sections of text. Paragraphs are rendered with default styling that typically includes some space above and below the paragraph, making it visually distinct from the surrounding content.",
            "examples": [
              "paragraph"
            ]
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Paragraph children."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Vertical padding. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "paragraphData": {
              "ref": "ParagraphData",
              "metadata": {
                "description": "Paragraph node attributes."
              }
            }
          }
        },
        "APP_EMBED": {
          "metadata": {
            "section": "App Embed",
            "examples": [
              "app-embed-event",
              "app-embed-booking"
            ],
            "features": [
              "APP_EMBED"
            ],
            "description": "Embeds an external content preview from Wix business solutions. For example, you can embed previews from the Wix Bookings, Wix Stores and Wix Events apps."
          },
          "properties": {
            "appEmbedData": {
              "ref": "AppEmbedData",
              "metadata": {
                "description": "Embedded Wix app."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "AUDIO": {
          "metadata": {
            "section": "Audio",
            "examples": [
              "audio-soundcloud"
            ],
            "features": [
              "AUDIO"
            ],
            "description": "Embeds and plays audio content directly in a webpage. Users can interact with the controls to play, pause, rewind, and adjust the volume of the audio."
          },
          "properties": {
            "audioData": {
              "ref": "AudioData",
              "metadata": {
                "description": "Audio details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "BUTTON": {
          "metadata": {
            "section": "Button",
            "examples": [
              "button-link",
              "button-action",
              "button-styled"
            ],
            "features": [
              "BUTTON"
            ],
            "description": "Button to trigger an action."
          },
          "properties": {
            "buttonData": {
              "ref": "ButtonData",
              "metadata": {
                "description": "Button details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "COLLAPSIBLE_LIST": {
          "metadata": {
            "section": "Collapsible List",
            "examples": [
              "collapsible-list"
            ],
            "features": [
              "COLLAPSIBLE_LIST"
            ],
            "description": "List of items where each item can be expanded or collapsed."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "CollapsibleItemNode"
              },
              "metadata": {
                "description": "Collapsible list child nodes.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "collapsibleListData": {
              "ref": "CollapsibleListData",
              "metadata": {
                "description": "Collapsible list details."
              }
            }
          }
        },
        "DIVIDER": {
          "metadata": {
            "section": "Divider",
            "examples": [
              "divider"
            ],
            "features": [
              "DIVIDER"
            ],
            "description": "Horizontal line between different content."
          },
          "properties": {},
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "dividerData": {
              "ref": "DividerData",
              "metadata": {
                "description": "Divider details."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "EMBED": {
          "metadata": {
            "section": "Embed",
            "examples": [
              "embed"
            ],
            "features": [
              "EMBED"
            ],
            "description": "Embedded content when you include a link to a resource, without having to parse the resource directly. This provides a standardized way for web developers to include content such as videos, images, and other multimedia elements from supported providers. To learn more, see [oembed](https://oembed.com/)."
          },
          "properties": {
            "embedData": {
              "ref": "EmbedData",
              "metadata": {
                "description": "oEmbed details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "FILE": {
          "metadata": {
            "section": "File",
            "examples": [
              "file"
            ],
            "features": [
              "FILE"
            ],
            "description": "File that is uploaded and accessed in a page. Files can be different types of content, such as text documents, images, and videos."
          },
          "properties": {
            "fileData": {
              "ref": "FileData",
              "metadata": {
                "description": "File details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "GALLERY": {
          "metadata": {
            "section": "Gallery",
            "examples": [
              "gallery"
            ],
            "features": [
              "GALLERY"
            ],
            "description": "Collection of images displayed together."
          },
          "properties": {
            "galleryData": {
              "ref": "GalleryData",
              "metadata": {
                "description": "Gallery node details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "GIF": {
          "metadata": {
            "section": "Gif",
            "examples": [
              "gif-basic"
            ],
            "features": [
              "GIF"
            ],
            "description": "GIF images."
          },
          "properties": {
            "gifData": {
              "ref": "GIFData",
              "metadata": {
                "description": "GIF details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define a GIF caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "HTML": {
          "metadata": {
            "section": "Html",
            "examples": [
              "html-code",
              "html-url"
            ],
            "features": [
              "HTML"
            ],
            "description": "HTML block that incorporates external content, such as interactive maps or entire web pages."
          },
          "properties": {
            "htmlData": {
              "ref": "HTMLData",
              "metadata": {
                "description": "HTML details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "IMAGE": {
          "metadata": {
            "section": "Image",
            "examples": [
              "image-id",
              "image-url"
            ],
            "features": [
              "IMAGE"
            ],
            "description": "Standalone images in a page."
          },
          "properties": {
            "imageData": {
              "ref": "ImageData",
              "metadata": {
                "description": "Image options."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define an image caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "LINK_PREVIEW": {
          "metadata": {
            "section": "Link Preview",
            "examples": [
              "link-preview"
            ],
            "features": [
              "LINK_PREVIEW"
            ],
            "description": "Preview or summary of a webpage. Link previews include elements such as the title of the linked page, a brief description or excerpt of the content, and sometimes a thumbnail image or other relevant information."
          },
          "properties": {
            "linkPreviewData": {
              "ref": "LinkPreviewData",
              "metadata": {
                "description": "Link preview details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "POLL": {
          "metadata": {
            "section": "Poll",
            "examples": [
              "poll"
            ],
            "features": [
              "POLL"
            ],
            "description": "Provides a question with several options, where the options' cumulative total may be shown."
          },
          "properties": {
            "pollData": {
              "ref": "PollData",
              "metadata": {
                "description": "Poll details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "VIDEO": {
          "metadata": {
            "section": "Video",
            "examples": [
              "video-url",
              "video-wix"
            ],
            "features": [
              "VIDEO"
            ],
            "description": "Embeds and plays video content directly in a page. When rendered, it is displayed as the video player with the specified video file. Users can interact with the controls to play, pause, rewind, and adjust the volume of the video."
          },
          "properties": {
            "videoData": {
              "ref": "VideoData",
              "metadata": {
                "description": "Video details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define a video caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        }
      }
    },
    "OrderedListNode": {
      "metadata": {
        "section": "List",
        "examples": [
          "orderedList"
        ],
        "description": "Numbered list."
      },
      "properties": {
        "type": {
          "enum": [
            "ORDERED_LIST"
          ],
          "metadata": {
            "description": "Node type. \n Must be `ORDERED_LIST`."
          }
        },
        "nodes": {
          "elements": {
            "ref": "ListItemNode",
            "metadata": {
              "validation": {
                "minSize": 1
              }
            }
          },
          "metadata": {
            "description": "List items.",
            "validation": {
              "minSize": 1
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "orderedListData": {
          "ref": "OrderedListData",
          "metadata": {
            "description": "Ordered list details."
          }
        }
      }
    },
    "OrderedListData": {
      "optionalProperties": {
        "indentation": {
          "type": "int32",
          "metadata": {
            "deprecated": true,
            "description": "List margin from the left side. \n The indentation is measured relative to the font size *em* units by a ratio of 1.5. For example, if the indentation is `2`, then the margin is equal to **3em**. Therefore the margin is 3 times the size of the font."
          }
        },
        "offset": {
          "type": "int32",
          "metadata": {
            "description": "Additional indentation of a list node. When offset is `0`, the list node appears at its default nesting level. Higher values cause increased indentation."
          }
        },
        "start": {
          "type": "int32",
          "metadata": {
            "description": "Number at the top of the numbered list. <br> Default: `1`"
          }
        }
      },
      "metadata": {
        "section": "List"
      }
    },
    "BulletedListNode": {
      "metadata": {
        "section": "List",
        "examples": [
          "bulletedList"
        ],
        "description": "Bulleted list."
      },
      "properties": {
        "type": {
          "enum": [
            "BULLETED_LIST"
          ],
          "metadata": {
            "description": "Node type. \n Must be `BULLETED_LIST`."
          }
        },
        "nodes": {
          "elements": {
            "ref": "ListItemNode",
            "metadata": {
              "validation": {
                "minSize": 1
              }
            }
          },
          "metadata": {
            "description": "List items.",
            "validation": {
              "minSize": 1
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "bulletedListData": {
          "ref": "BulletedListData",
          "metadata": {
            "description": "Bulleted list details."
          }
        }
      }
    },
    "BulletedListData": {
      "optionalProperties": {
        "indentation": {
          "type": "int32",
          "metadata": {
            "deprecated": true
          }
        },
        "offset": {
          "type": "int32",
          "metadata": {
            "description": "Additional indentation of a list node. When offset is `0`, the list node appears at its default nesting level. Higher values cause increased indentation."
          }
        }
      },
      "metadata": {
        "section": "List"
      }
    },
    "ListItemNode": {
      "properties": {
        "type": {
          "enum": [
            "LIST_ITEM"
          ],
          "metadata": {
            "description": "Node type. \n Value: `LIST_ITEM`"
          }
        },
        "nodes": {
          "elements": {
            "ref": "ListItemChildNode"
          },
          "metadata": {
            "description": "Child nodes that the list item contains.",
            "pattern": {
              "description": "Must start with a paragraph or heading, followed by any number of paragraphs, bulleted lists, or ordered lists.",
              "value": "(PARAGRAPH|HEADING|IMAGE|VIDEO|GIF|GALLERY) (PARAGRAPH|BULLETED_LIST|ORDERED_LIST|HEADING|IMAGE|VIDEO|GIF|GALLERY)*"
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        }
      },
      "metadata": {
        "section": "List"
      }
    },
    "ListItemChildNode": {
      "discriminator": "type",
      "metadata": {
        "section": "List",
        "refUnion": {
          "PARAGRAPH": "ParagraphNode",
          "HEADING": "HeadingNode",
          "ORDERED_LIST": "OrderedListNode",
          "BULLETED_LIST": "BulletedListNode",
          "IMAGE": "ImageNode",
          "VIDEO": "VideoNode",
          "GIF": "GifNode",
          "GALLERY": "GalleryNode"
        }
      },
      "mapping": {
        "PARAGRAPH": {
          "metadata": {
            "section": "Paragraph",
            "description": "Structures text content, providing a clear separation between different ideas or sections of text. Paragraphs are rendered with default styling that typically includes some space above and below the paragraph, making it visually distinct from the surrounding content.",
            "examples": [
              "paragraph"
            ]
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Paragraph children."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Vertical padding. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "paragraphData": {
              "ref": "ParagraphData",
              "metadata": {
                "description": "Paragraph node attributes."
              }
            }
          }
        },
        "HEADING": {
          "metadata": {
            "section": "Heading",
            "examples": [
              "headings-all-sizes"
            ],
            "description": "Indicates a heading or title of a section. There are 6 levels of headings, with `1` being the most prominent and `6` being the least."
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Child nodes to define the heading text."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Heading styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "headingData": {
              "ref": "HeadingData",
              "metadata": {
                "description": "Heading options."
              }
            }
          }
        },
        "ORDERED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "orderedList"
            ],
            "description": "Numbered list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "orderedListData": {
              "ref": "OrderedListData",
              "metadata": {
                "description": "Ordered list details."
              }
            }
          }
        },
        "BULLETED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "bulletedList"
            ],
            "description": "Bulleted list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "bulletedListData": {
              "ref": "BulletedListData",
              "metadata": {
                "description": "Bulleted list details."
              }
            }
          }
        },
        "IMAGE": {
          "metadata": {
            "section": "Image",
            "examples": [
              "image-id",
              "image-url"
            ],
            "features": [
              "IMAGE"
            ],
            "description": "Standalone images in a page."
          },
          "properties": {
            "imageData": {
              "ref": "ImageData",
              "metadata": {
                "description": "Image options."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define an image caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "VIDEO": {
          "metadata": {
            "section": "Video",
            "examples": [
              "video-url",
              "video-wix"
            ],
            "features": [
              "VIDEO"
            ],
            "description": "Embeds and plays video content directly in a page. When rendered, it is displayed as the video player with the specified video file. Users can interact with the controls to play, pause, rewind, and adjust the volume of the video."
          },
          "properties": {
            "videoData": {
              "ref": "VideoData",
              "metadata": {
                "description": "Video details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define a video caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "GIF": {
          "metadata": {
            "section": "Gif",
            "examples": [
              "gif-basic"
            ],
            "features": [
              "GIF"
            ],
            "description": "GIF images."
          },
          "properties": {
            "gifData": {
              "ref": "GIFData",
              "metadata": {
                "description": "GIF details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define a GIF caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "GALLERY": {
          "metadata": {
            "section": "Gallery",
            "examples": [
              "gallery"
            ],
            "features": [
              "GALLERY"
            ],
            "description": "Collection of images displayed together."
          },
          "properties": {
            "galleryData": {
              "ref": "GalleryData",
              "metadata": {
                "description": "Gallery node details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        }
      }
    },
    "ParagraphNode": {
      "metadata": {
        "section": "Paragraph",
        "description": "Structures text content, providing a clear separation between different ideas or sections of text. Paragraphs are rendered with default styling that typically includes some space above and below the paragraph, making it visually distinct from the surrounding content.",
        "examples": [
          "paragraph"
        ]
      },
      "properties": {
        "type": {
          "enum": [
            "PARAGRAPH"
          ],
          "metadata": {
            "description": "Node type. \n Must be `PARAGRAPH`."
          }
        }
      },
      "optionalProperties": {
        "nodes": {
          "elements": {
            "ref": "TextNode"
          },
          "metadata": {
            "description": "Paragraph children."
          }
        },
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "style": {
          "ref": "NodeStyle",
          "metadata": {
            "description": "Vertical padding. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
          }
        },
        "paragraphData": {
          "ref": "ParagraphData",
          "metadata": {
            "description": "Paragraph node attributes."
          }
        }
      }
    },
    "ParagraphData": {
      "optionalProperties": {
        "textStyle": {
          "ref": "TextStyle",
          "metadata": {
            "description": "Paragraph text styling."
          }
        },
        "indentation": {
          "type": "int32",
          "metadata": {
            "description": "Paragraph margin from the left side. \n The indentation is measured relative to 40px. For example, if the indentation is `2`, then the margin is equal to **80px**."
          }
        },
        "level": {
          "type": "int32",
          "metadata": {
            "internal": true,
            "description": "Paragraph level for future different paragraphs."
          }
        }
      },
      "metadata": {
        "section": "Paragraph"
      }
    },
    "TextNode": {
      "metadata": {
        "section": "TextNode",
        "examples": [
          "text"
        ],
        "description": "Text nodes hold the text itself, similar to how `span` tags hold text in HTML. Text nodes can't be nested and must contain a non-empty string. Their appearance and behavior can be modified using [decorations](#decorations)."
      },
      "properties": {
        "type": {
          "enum": [
            "TEXT"
          ],
          "metadata": {
            "description": "Node type. \n Must be `TEXT`."
          }
        },
        "textData": {
          "ref": "TextData",
          "metadata": {
            "description": "Text options."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "internal": true
          },
          "elements": {
            "ref": "Never"
          }
        }
      }
    },
    "TextData": {
      "properties": {
        "text": {
          "type": "string",
          "metadata": {
            "description": "Non-empty string to display as text.",
            "validation": {
              "minLength": 1
            }
          }
        }
      },
      "optionalProperties": {
        "decorations": {
          "elements": {
            "ref": "Decoration"
          },
          "metadata": {
            "description": "Array of text [decorations](#decorations). A decoration can't appear twice in the array.",
            "validation": {
              "uniqueTags": true
            }
          }
        }
      },
      "metadata": {
        "section": "TextNode"
      }
    },
    "AppEmbedNode": {
      "metadata": {
        "section": "App Embed",
        "examples": [
          "app-embed-event",
          "app-embed-booking"
        ],
        "features": [
          "APP_EMBED"
        ],
        "description": "Embeds an external content preview from Wix business solutions. For example, you can embed previews from the Wix Bookings, Wix Stores and Wix Events apps."
      },
      "properties": {
        "type": {
          "enum": [
            "APP_EMBED"
          ],
          "metadata": {
            "description": "Node type. \n Must be `APP_EMBED`."
          }
        },
        "appEmbedData": {
          "ref": "AppEmbedData",
          "metadata": {
            "description": "Embedded Wix app."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "internal": true
          },
          "elements": {
            "ref": "Never"
          }
        }
      }
    },
    "AppEmbedData": {
      "discriminator": "type",
      "metadata": {
        "section": "App Embed",
        "refUnion": {
          "PRODUCT": "AppEmbedDataProduct",
          "EVENT": "AppEmbedDataEvent",
          "BOOKING": "AppEmbedDataBooking"
        },
        "emitEnum": "AppEmbedData_AppType"
      },
      "mapping": {
        "PRODUCT": {
          "metadata": {
            "section": "App Embed",
            "description": "Embedded a product from Wix Stores."
          },
          "properties": {
            "name": {
              "type": "string",
              "metadata": {
                "description": "Embedded content name."
              }
            },
            "url": {
              "type": "string",
              "metadata": {
                "description": "Embedded content URL."
              }
            }
          },
          "optionalProperties": {
            "image": {
              "ref": "Media",
              "metadata": {
                "description": "Embedded content thumbnail."
              }
            },
            "itemId": {
              "type": "string",
              "metadata": {
                "description": "Embedded content ID."
              }
            },
            "imageSrc": {
              "type": "string",
              "metadata": {
                "description": "Deprecated: Use `image` instead.",
                "deprecated": true
              }
            }
          }
        },
        "EVENT": {
          "metadata": {
            "section": "App Embed",
            "description": "Embeds an event from Wix Events."
          },
          "properties": {
            "name": {
              "type": "string",
              "metadata": {
                "description": "Embedded content name."
              }
            },
            "url": {
              "type": "string",
              "metadata": {
                "description": "Embedded content URL."
              }
            }
          },
          "optionalProperties": {
            "eventData": {
              "ref": "AppEmbedData_EventData",
              "metadata": {
                "description": "Embedded event content."
              }
            },
            "image": {
              "ref": "Media",
              "metadata": {
                "description": "Embedded content thumbnail."
              }
            },
            "itemId": {
              "type": "string",
              "metadata": {
                "description": "Embedded content ID."
              }
            },
            "imageSrc": {
              "type": "string",
              "metadata": {
                "description": "Deprecated: Use `image` instead.",
                "deprecated": true
              }
            }
          }
        },
        "BOOKING": {
          "metadata": {
            "section": "App Embed",
            "description": "Embeds a booking from Wix Bookings."
          },
          "properties": {
            "name": {
              "type": "string",
              "metadata": {
                "description": "Embedded content name."
              }
            },
            "url": {
              "type": "string",
              "metadata": {
                "description": "Embedded content URL."
              }
            }
          },
          "optionalProperties": {
            "image": {
              "ref": "Media",
              "metadata": {
                "description": "Embedded content thumbnail."
              }
            },
            "itemId": {
              "type": "string",
              "metadata": {
                "description": "Embedded content ID."
              }
            },
            "bookingData": {
              "ref": "AppEmbedData_BookingData",
              "metadata": {
                "description": "Embedded booking content."
              }
            },
            "imageSrc": {
              "type": "string",
              "metadata": {
                "description": "Deprecated: Use `image` instead.",
                "deprecated": true
              }
            }
          }
        }
      }
    },
    "AppEmbedDataProduct": {
      "metadata": {
        "section": "App Embed",
        "description": "Embedded a product from Wix Stores."
      },
      "properties": {
        "type": {
          "enum": [
            "PRODUCT"
          ],
          "metadata": {
            "description": "Must be `PRODUCT`."
          }
        },
        "name": {
          "type": "string",
          "metadata": {
            "description": "Embedded content name."
          }
        },
        "url": {
          "type": "string",
          "metadata": {
            "description": "Embedded content URL."
          }
        }
      },
      "optionalProperties": {
        "image": {
          "ref": "Media",
          "metadata": {
            "description": "Embedded content thumbnail."
          }
        },
        "itemId": {
          "type": "string",
          "metadata": {
            "description": "Embedded content ID."
          }
        },
        "imageSrc": {
          "type": "string",
          "metadata": {
            "description": "Deprecated: Use `image` instead.",
            "deprecated": true
          }
        }
      }
    },
    "AppEmbedDataEvent": {
      "metadata": {
        "section": "App Embed",
        "description": "Embeds an event from Wix Events."
      },
      "properties": {
        "type": {
          "enum": [
            "EVENT"
          ],
          "metadata": {
            "description": "Must be `EVENT`."
          }
        },
        "name": {
          "type": "string",
          "metadata": {
            "description": "Embedded content name."
          }
        },
        "url": {
          "type": "string",
          "metadata": {
            "description": "Embedded content URL."
          }
        }
      },
      "optionalProperties": {
        "eventData": {
          "ref": "AppEmbedData_EventData",
          "metadata": {
            "description": "Embedded event content."
          }
        },
        "image": {
          "ref": "Media",
          "metadata": {
            "description": "Embedded content thumbnail."
          }
        },
        "itemId": {
          "type": "string",
          "metadata": {
            "description": "Embedded content ID."
          }
        },
        "imageSrc": {
          "type": "string",
          "metadata": {
            "description": "Deprecated: Use `image` instead.",
            "deprecated": true
          }
        }
      }
    },
    "AppEmbedDataBooking": {
      "metadata": {
        "section": "App Embed",
        "description": "Embeds a booking from Wix Bookings."
      },
      "properties": {
        "type": {
          "enum": [
            "BOOKING"
          ]
        },
        "name": {
          "type": "string",
          "metadata": {
            "description": "Embedded content name."
          }
        },
        "url": {
          "type": "string",
          "metadata": {
            "description": "Embedded content URL."
          }
        }
      },
      "optionalProperties": {
        "image": {
          "ref": "Media",
          "metadata": {
            "description": "Embedded content thumbnail."
          }
        },
        "itemId": {
          "type": "string",
          "metadata": {
            "description": "Embedded content ID."
          }
        },
        "bookingData": {
          "ref": "AppEmbedData_BookingData",
          "metadata": {
            "description": "Embedded booking content."
          }
        },
        "imageSrc": {
          "type": "string",
          "metadata": {
            "description": "Deprecated: Use `image` instead.",
            "deprecated": true
          }
        }
      }
    },
    "AppEmbedData_BookingData": {
      "optionalProperties": {
        "durations": {
          "type": "string",
          "metadata": {
            "description": "Booking duration in minutes."
          }
        }
      },
      "metadata": {
        "section": "App Embed"
      }
    },
    "AppEmbedData_EventData": {
      "optionalProperties": {
        "scheduling": {
          "type": "string",
          "metadata": {
            "description": "Event schedule."
          }
        },
        "location": {
          "type": "string",
          "metadata": {
            "description": "Event location."
          }
        }
      },
      "metadata": {
        "section": "App Embed"
      }
    },
    "AudioNode": {
      "metadata": {
        "section": "Audio",
        "examples": [
          "audio-soundcloud"
        ],
        "features": [
          "AUDIO"
        ],
        "description": "Embeds and plays audio content directly in a webpage. Users can interact with the controls to play, pause, rewind, and adjust the volume of the audio."
      },
      "properties": {
        "type": {
          "enum": [
            "AUDIO"
          ],
          "metadata": {
            "description": "Node type. \n Must be `AUDIO`."
          }
        },
        "audioData": {
          "ref": "AudioData",
          "metadata": {
            "description": "Audio details."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "internal": true
          },
          "elements": {
            "ref": "Never"
          }
        }
      }
    },
    "AudioData": {
      "optionalProperties": {
        "containerData": {
          "metadata": {
            "description": "Audio node container styling."
          },
          "ref": "PluginContainerData"
        },
        "audio": {
          "metadata": {
            "description": "Audio file details."
          },
          "ref": "Media"
        },
        "disableDownload": {
          "metadata": {
            "description": "Whether the audio download button is disabled. \n Default: `false`"
          },
          "type": "boolean"
        },
        "coverImage": {
          "metadata": {
            "description": "Cover image."
          },
          "ref": "Media"
        },
        "name": {
          "metadata": {
            "description": "Track name."
          },
          "type": "string"
        },
        "authorName": {
          "metadata": {
            "description": "Author name."
          },
          "type": "string"
        },
        "html": {
          "metadata": {
            "description": "HTML version of the audio node."
          },
          "type": "string"
        }
      },
      "metadata": {
        "section": "Audio"
      }
    },
    "ButtonNode": {
      "metadata": {
        "section": "Button",
        "examples": [
          "button-link",
          "button-action",
          "button-styled"
        ],
        "features": [
          "BUTTON"
        ],
        "description": "Button to trigger an action."
      },
      "properties": {
        "type": {
          "enum": [
            "BUTTON"
          ],
          "metadata": {
            "description": "Node type. \n Must be `BUTTON`"
          }
        },
        "buttonData": {
          "ref": "ButtonData",
          "metadata": {
            "description": "Button details."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "internal": true
          },
          "elements": {
            "ref": "Never"
          }
        }
      }
    },
    "ButtonData": {
      "properties": {
        "type": {
          "enum": [
            "LINK",
            "ACTION"
          ],
          "metadata": {
            "emitEnum": "ButtonData_Type",
            "description": "Button type. Supported values: <br> - `LINK`: Redirects to a webpage. <br> - `ACTION`: Performs an action."
          }
        },
        "text": {
          "type": "string",
          "metadata": {
            "description": "Button text."
          }
        }
      },
      "optionalProperties": {
        "containerData": {
          "ref": "PluginContainerData",
          "metadata": {
            "description": "Button container styling."
          }
        },
        "styles": {
          "ref": "ButtonData_Styles",
          "metadata": {
            "description": "Button styling."
          }
        },
        "link": {
          "ref": "Link",
          "metadata": {
            "description": "Button link details."
          }
        }
      },
      "metadata": {
        "section": "Button"
      }
    },
    "ButtonData_Styles": {
      "optionalProperties": {
        "border": {
          "ref": "ButtonData_Styles_Border",
          "metadata": {
            "description": "Deprecated: Use `borderWidth` and `borderRadius` instead.",
            "deprecated": true
          }
        },
        "colors": {
          "ref": "ButtonData_Styles_Colors",
          "metadata": {
            "description": "Deprecated: Use `textColor`, `borderColor` and `backgroundColor` instead.",
            "deprecated": true
          }
        },
        "borderWidth": {
          "type": "int32",
          "metadata": {
            "description": "Border width in pixels."
          }
        },
        "borderWidthHover": {
          "type": "int32",
          "metadata": {
            "description": "Border width in pixels (hover state)."
          }
        },
        "borderRadius": {
          "type": "int32",
          "metadata": {
            "description": "Border radius in pixels."
          }
        },
        "borderColor": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Border color as a hexadecimal value."
          }
        },
        "borderColorHover": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Border color as a hexadecimal value (hover state)."
          }
        },
        "textColor": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Text color as a hexadecimal value."
          }
        },
        "textColorHover": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Text color as a hexadecimal value (hover state)."
          }
        },
        "backgroundColor": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Background color as a hexadecimal value."
          }
        },
        "backgroundColorHover": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Background color as a hexadecimal value (hover state)."
          }
        },
        "buttonSize": {
          "enum": [
            "MEDIUM",
            "SMALL",
            "LARGE"
          ],
          "metadata": {
            "emitEnum": "ButtonData_Styles_ButtonSize",
            "description": "Button size. Defaults to `MEDIUM`."
          }
        }
      },
      "metadata": {
        "section": "Button"
      }
    },
    "ButtonData_Styles_Colors": {
      "optionalProperties": {
        "text": {
          "type": "string",
          "metadata": {
            "description": "Deprecated: Use `textColor` in `styles` instead.",
            "deprecated": true
          }
        },
        "border": {
          "type": "string",
          "metadata": {
            "description": "Deprecated: Use `borderColor` in `styles` instead.",
            "deprecated": true
          }
        },
        "background": {
          "type": "string",
          "metadata": {
            "description": "Deprecated: Use `backgroundColor` in `styles` instead.",
            "deprecated": true
          }
        }
      },
      "metadata": {
        "section": "Button"
      }
    },
    "ButtonData_Styles_Border": {
      "optionalProperties": {
        "width": {
          "type": "int32",
          "metadata": {
            "description": "Deprecated: Use `borderWidth` in `styles` instead.",
            "deprecated": true
          }
        },
        "radius": {
          "type": "int32",
          "metadata": {
            "description": "Deprecated: Use `borderRadius` in `styles` instead",
            "deprecated": true
          }
        }
      },
      "metadata": {
        "section": "Button"
      }
    },
    "CollapsibleListNode": {
      "metadata": {
        "section": "Collapsible List",
        "examples": [
          "collapsible-list"
        ],
        "features": [
          "COLLAPSIBLE_LIST"
        ],
        "description": "List of items where each item can be expanded or collapsed."
      },
      "properties": {
        "type": {
          "enum": [
            "COLLAPSIBLE_LIST"
          ],
          "metadata": {
            "description": "Node type. \n Must be `COLLAPSIBLE_LIST`"
          }
        },
        "nodes": {
          "elements": {
            "ref": "CollapsibleItemNode"
          },
          "metadata": {
            "description": "Collapsible list child nodes.",
            "validation": {
              "minSize": 1
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "collapsibleListData": {
          "ref": "CollapsibleListData",
          "metadata": {
            "description": "Collapsible list details."
          }
        }
      }
    },
    "CollapsibleListData": {
      "optionalProperties": {
        "containerData": {
          "ref": "PluginContainerData",
          "metadata": {
            "description": "Collapsible list container styling."
          }
        },
        "expandOnlyOne": {
          "type": "boolean",
          "metadata": {
            "description": "Whether only one item can be expanded at a time. \n Default: `false`."
          }
        },
        "initialExpandedItems": {
          "enum": [
            "FIRST",
            "ALL",
            "NONE"
          ],
          "metadata": {
            "metadata": {
              "emitEnum": "CollapsibleListData_InitialExpandedItems"
            },
            "description": "Sets which items are automatically expanded after the page loads."
          }
        },
        "direction": {
          "enum": [
            "LTR",
            "RTL"
          ],
          "metadata": {
            "emitEnum": "CollapsibleListData_Direction",
            "description": "Direction of the text in the list. Supported values: \n - `LTR`: Left to right. \n - `RTL`: Right to left."
          }
        },
        "isQapageData": {
          "type": "boolean",
          "metadata": {
            "description": "Whether the collapsible item will appear in search results as an FAQ."
          }
        }
      },
      "metadata": {
        "section": "Collapsible List"
      }
    },
    "CollapsibleItemNode": {
      "properties": {
        "type": {
          "enum": [
            "COLLAPSIBLE_ITEM"
          ],
          "metadata": {
            "description": "Node type. \n Must be `COLLAPSIBLE_ITEM`"
          }
        },
        "nodes": {
          "elements": {
            "ref": "CollapsibleItemChildNode"
          },
          "metadata": {
            "description": "Collapsible list item.",
            "pattern": {
              "value": "COLLAPSIBLE_ITEM_TITLE COLLAPSIBLE_ITEM_BODY",
              "description": "Must contain title as the first item and body as the second."
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        }
      },
      "metadata": {
        "section": "Collapsible List"
      }
    },
    "CollapsibleItemChildNode": {
      "discriminator": "type",
      "metadata": {
        "section": "Collapsible List",
        "refUnion": {
          "COLLAPSIBLE_ITEM_TITLE": "CollapsibleItemTitleNode",
          "COLLAPSIBLE_ITEM_BODY": "CollapsibleItemBodyNode"
        }
      },
      "mapping": {
        "COLLAPSIBLE_ITEM_TITLE": {
          "metadata": {
            "section": "Collapsible List",
            "description": "Collapsible list item title."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "CollapsibleTitleChildNode"
              },
              "metadata": {
                "description": "Collapsible list item title child nodes.",
                "validation": {
                  "minSize": 1,
                  "maxSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            }
          }
        },
        "COLLAPSIBLE_ITEM_BODY": {
          "metadata": {
            "section": "Collapsible List",
            "description": "Collapsible list item body."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "CollapsibleItemBodyChildNode"
              },
              "metadata": {
                "description": "Collapsible list item body child nodes.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            }
          }
        }
      }
    },
    "CollapsibleItemTitleNode": {
      "metadata": {
        "section": "Collapsible List",
        "description": "Collapsible list item title."
      },
      "properties": {
        "type": {
          "enum": [
            "COLLAPSIBLE_ITEM_TITLE"
          ],
          "metadata": {
            "description": "Node type. \n Must be `COLLAPSIBLE_ITEM_TITLE`."
          }
        },
        "nodes": {
          "elements": {
            "ref": "CollapsibleTitleChildNode"
          },
          "metadata": {
            "description": "Collapsible list item title child nodes.",
            "validation": {
              "minSize": 1,
              "maxSize": 1
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        }
      }
    },
    "CollapsibleTitleChildNode": {
      "discriminator": "type",
      "metadata": {
        "section": "Collapsible List",
        "refUnion": {
          "PARAGRAPH": "ParagraphNode",
          "HEADING": "HeadingNode",
          "BULLETED_LIST": "BulletedListNode",
          "ORDERED_LIST": "OrderedListNode",
          "BLOCKQUOTE": "BlockquoteNode",
          "CODE_BLOCK": "CodeBlockNode"
        }
      },
      "mapping": {
        "PARAGRAPH": {
          "metadata": {
            "section": "Paragraph",
            "description": "Structures text content, providing a clear separation between different ideas or sections of text. Paragraphs are rendered with default styling that typically includes some space above and below the paragraph, making it visually distinct from the surrounding content.",
            "examples": [
              "paragraph"
            ]
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Paragraph children."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Vertical padding. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "paragraphData": {
              "ref": "ParagraphData",
              "metadata": {
                "description": "Paragraph node attributes."
              }
            }
          }
        },
        "HEADING": {
          "metadata": {
            "section": "Heading",
            "examples": [
              "headings-all-sizes"
            ],
            "description": "Indicates a heading or title of a section. There are 6 levels of headings, with `1` being the most prominent and `6` being the least."
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Child nodes to define the heading text."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Heading styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "headingData": {
              "ref": "HeadingData",
              "metadata": {
                "description": "Heading options."
              }
            }
          }
        },
        "BULLETED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "bulletedList"
            ],
            "description": "Bulleted list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "bulletedListData": {
              "ref": "BulletedListData",
              "metadata": {
                "description": "Bulleted list details."
              }
            }
          }
        },
        "ORDERED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "orderedList"
            ],
            "description": "Numbered list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "orderedListData": {
              "ref": "OrderedListData",
              "metadata": {
                "description": "Ordered list details."
              }
            }
          }
        },
        "BLOCKQUOTE": {
          "metadata": {
            "section": "Block quote",
            "examples": [
              "blockquote"
            ],
            "description": "Used to visually distinguish quoted or cited content from the surrounding text. When rendered, it displays as the quoted text with indentation or other styling to visually separate it from the surrounding content."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ParagraphNode"
              },
              "metadata": {
                "description": "Child nodes to define a blockquote paragraph.",
                "validation": {
                  "minSize": 1,
                  "maxSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "blockquoteData": {
              "ref": "BlockquoteData",
              "metadata": {
                "description": "Blockquote details."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Defines the blockquote style, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            }
          }
        },
        "CODE_BLOCK": {
          "metadata": {
            "section": "Codeblock",
            "features": [
              "CODE_BLOCK"
            ],
            "examples": [
              "code-block-multi-line",
              "code-block-syntax-highlighting"
            ],
            "description": "Presents code snippets, markup, or other text-based content that should be displayed exactly as it appears, without any interpretation or formatting applied by the browser. When rendered, it is displayed as the code block with a monospaced font, preserving indentation, line breaks, and spacing as specified in the code."
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Child nodes to define a code block text."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "codeBlockData": {
              "ref": "CodeBlockData",
              "metadata": {
                "description": "Code block details."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Code block styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            }
          }
        }
      }
    },
    "CollapsibleItemBodyNode": {
      "metadata": {
        "section": "Collapsible List",
        "description": "Collapsible list item body."
      },
      "properties": {
        "type": {
          "enum": [
            "COLLAPSIBLE_ITEM_BODY"
          ],
          "metadata": {
            "description": "Node type. \n Must be `COLLAPSIBLE_ITEM_BODY`."
          }
        },
        "nodes": {
          "elements": {
            "ref": "CollapsibleItemBodyChildNode"
          },
          "metadata": {
            "description": "Collapsible list item body child nodes.",
            "validation": {
              "minSize": 1
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        }
      }
    },
    "CollapsibleItemBodyChildNode": {
      "discriminator": "type",
      "metadata": {
        "section": "Collapsible List",
        "refUnion": {
          "PARAGRAPH": "ParagraphNode",
          "HEADING": "HeadingNode",
          "BULLETED_LIST": "BulletedListNode",
          "ORDERED_LIST": "OrderedListNode",
          "BLOCKQUOTE": "BlockquoteNode",
          "AUDIO": "AudioNode",
          "FILE": "FileNode",
          "IMAGE": "ImageNode",
          "VIDEO": "VideoNode",
          "GIF": "GifNode",
          "DIVIDER": "DividerNode",
          "CODE_BLOCK": "CodeBlockNode",
          "APP_EMBED": "AppEmbedNode",
          "LINK_PREVIEW": "LinkPreviewNode",
          "GALLERY": "GalleryNode",
          "BUTTON": "ButtonNode",
          "COLLAPSIBLE_LIST": "CollapsibleListNode",
          "TABLE": "TableNode",
          "EMBED": "EmbedNode",
          "POLL": "PollNode",
          "HTML": "HtmlNode"
        },
        "description": "Collapsible list item body."
      },
      "mapping": {
        "PARAGRAPH": {
          "metadata": {
            "section": "Paragraph",
            "description": "Structures text content, providing a clear separation between different ideas or sections of text. Paragraphs are rendered with default styling that typically includes some space above and below the paragraph, making it visually distinct from the surrounding content.",
            "examples": [
              "paragraph"
            ]
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Paragraph children."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Vertical padding. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "paragraphData": {
              "ref": "ParagraphData",
              "metadata": {
                "description": "Paragraph node attributes."
              }
            }
          }
        },
        "HEADING": {
          "metadata": {
            "section": "Heading",
            "examples": [
              "headings-all-sizes"
            ],
            "description": "Indicates a heading or title of a section. There are 6 levels of headings, with `1` being the most prominent and `6` being the least."
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Child nodes to define the heading text."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Heading styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "headingData": {
              "ref": "HeadingData",
              "metadata": {
                "description": "Heading options."
              }
            }
          }
        },
        "BULLETED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "bulletedList"
            ],
            "description": "Bulleted list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "bulletedListData": {
              "ref": "BulletedListData",
              "metadata": {
                "description": "Bulleted list details."
              }
            }
          }
        },
        "ORDERED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "orderedList"
            ],
            "description": "Numbered list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "orderedListData": {
              "ref": "OrderedListData",
              "metadata": {
                "description": "Ordered list details."
              }
            }
          }
        },
        "BLOCKQUOTE": {
          "metadata": {
            "section": "Block quote",
            "examples": [
              "blockquote"
            ],
            "description": "Used to visually distinguish quoted or cited content from the surrounding text. When rendered, it displays as the quoted text with indentation or other styling to visually separate it from the surrounding content."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ParagraphNode"
              },
              "metadata": {
                "description": "Child nodes to define a blockquote paragraph.",
                "validation": {
                  "minSize": 1,
                  "maxSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "blockquoteData": {
              "ref": "BlockquoteData",
              "metadata": {
                "description": "Blockquote details."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Defines the blockquote style, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            }
          }
        },
        "AUDIO": {
          "metadata": {
            "section": "Audio",
            "examples": [
              "audio-soundcloud"
            ],
            "features": [
              "AUDIO"
            ],
            "description": "Embeds and plays audio content directly in a webpage. Users can interact with the controls to play, pause, rewind, and adjust the volume of the audio."
          },
          "properties": {
            "audioData": {
              "ref": "AudioData",
              "metadata": {
                "description": "Audio details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "FILE": {
          "metadata": {
            "section": "File",
            "examples": [
              "file"
            ],
            "features": [
              "FILE"
            ],
            "description": "File that is uploaded and accessed in a page. Files can be different types of content, such as text documents, images, and videos."
          },
          "properties": {
            "fileData": {
              "ref": "FileData",
              "metadata": {
                "description": "File details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "IMAGE": {
          "metadata": {
            "section": "Image",
            "examples": [
              "image-id",
              "image-url"
            ],
            "features": [
              "IMAGE"
            ],
            "description": "Standalone images in a page."
          },
          "properties": {
            "imageData": {
              "ref": "ImageData",
              "metadata": {
                "description": "Image options."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define an image caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "VIDEO": {
          "metadata": {
            "section": "Video",
            "examples": [
              "video-url",
              "video-wix"
            ],
            "features": [
              "VIDEO"
            ],
            "description": "Embeds and plays video content directly in a page. When rendered, it is displayed as the video player with the specified video file. Users can interact with the controls to play, pause, rewind, and adjust the volume of the video."
          },
          "properties": {
            "videoData": {
              "ref": "VideoData",
              "metadata": {
                "description": "Video details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define a video caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "GIF": {
          "metadata": {
            "section": "Gif",
            "examples": [
              "gif-basic"
            ],
            "features": [
              "GIF"
            ],
            "description": "GIF images."
          },
          "properties": {
            "gifData": {
              "ref": "GIFData",
              "metadata": {
                "description": "GIF details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define a GIF caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "DIVIDER": {
          "metadata": {
            "section": "Divider",
            "examples": [
              "divider"
            ],
            "features": [
              "DIVIDER"
            ],
            "description": "Horizontal line between different content."
          },
          "properties": {},
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "dividerData": {
              "ref": "DividerData",
              "metadata": {
                "description": "Divider details."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "CODE_BLOCK": {
          "metadata": {
            "section": "Codeblock",
            "features": [
              "CODE_BLOCK"
            ],
            "examples": [
              "code-block-multi-line",
              "code-block-syntax-highlighting"
            ],
            "description": "Presents code snippets, markup, or other text-based content that should be displayed exactly as it appears, without any interpretation or formatting applied by the browser. When rendered, it is displayed as the code block with a monospaced font, preserving indentation, line breaks, and spacing as specified in the code."
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Child nodes to define a code block text."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "codeBlockData": {
              "ref": "CodeBlockData",
              "metadata": {
                "description": "Code block details."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Code block styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            }
          }
        },
        "APP_EMBED": {
          "metadata": {
            "section": "App Embed",
            "examples": [
              "app-embed-event",
              "app-embed-booking"
            ],
            "features": [
              "APP_EMBED"
            ],
            "description": "Embeds an external content preview from Wix business solutions. For example, you can embed previews from the Wix Bookings, Wix Stores and Wix Events apps."
          },
          "properties": {
            "appEmbedData": {
              "ref": "AppEmbedData",
              "metadata": {
                "description": "Embedded Wix app."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "LINK_PREVIEW": {
          "metadata": {
            "section": "Link Preview",
            "examples": [
              "link-preview"
            ],
            "features": [
              "LINK_PREVIEW"
            ],
            "description": "Preview or summary of a webpage. Link previews include elements such as the title of the linked page, a brief description or excerpt of the content, and sometimes a thumbnail image or other relevant information."
          },
          "properties": {
            "linkPreviewData": {
              "ref": "LinkPreviewData",
              "metadata": {
                "description": "Link preview details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "GALLERY": {
          "metadata": {
            "section": "Gallery",
            "examples": [
              "gallery"
            ],
            "features": [
              "GALLERY"
            ],
            "description": "Collection of images displayed together."
          },
          "properties": {
            "galleryData": {
              "ref": "GalleryData",
              "metadata": {
                "description": "Gallery node details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "BUTTON": {
          "metadata": {
            "section": "Button",
            "examples": [
              "button-link",
              "button-action",
              "button-styled"
            ],
            "features": [
              "BUTTON"
            ],
            "description": "Button to trigger an action."
          },
          "properties": {
            "buttonData": {
              "ref": "ButtonData",
              "metadata": {
                "description": "Button details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "COLLAPSIBLE_LIST": {
          "metadata": {
            "section": "Collapsible List",
            "examples": [
              "collapsible-list"
            ],
            "features": [
              "COLLAPSIBLE_LIST"
            ],
            "description": "List of items where each item can be expanded or collapsed."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "CollapsibleItemNode"
              },
              "metadata": {
                "description": "Collapsible list child nodes.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "collapsibleListData": {
              "ref": "CollapsibleListData",
              "metadata": {
                "description": "Collapsible list details."
              }
            }
          }
        },
        "TABLE": {
          "metadata": {
            "section": "Table",
            "examples": [
              "table"
            ],
            "features": [
              "TABLE"
            ],
            "description": "Data in rows and columns."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "TableRowNode"
              },
              "metadata": {
                "description": "Child nodes to define table rows.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "tableData": {
              "ref": "TableData",
              "metadata": {
                "description": "Table details."
              }
            }
          }
        },
        "EMBED": {
          "metadata": {
            "section": "Embed",
            "examples": [
              "embed"
            ],
            "features": [
              "EMBED"
            ],
            "description": "Embedded content when you include a link to a resource, without having to parse the resource directly. This provides a standardized way for web developers to include content such as videos, images, and other multimedia elements from supported providers. To learn more, see [oembed](https://oembed.com/)."
          },
          "properties": {
            "embedData": {
              "ref": "EmbedData",
              "metadata": {
                "description": "oEmbed details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "POLL": {
          "metadata": {
            "section": "Poll",
            "examples": [
              "poll"
            ],
            "features": [
              "POLL"
            ],
            "description": "Provides a question with several options, where the options' cumulative total may be shown."
          },
          "properties": {
            "pollData": {
              "ref": "PollData",
              "metadata": {
                "description": "Poll details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "HTML": {
          "metadata": {
            "section": "Html",
            "examples": [
              "html-code",
              "html-url"
            ],
            "features": [
              "HTML"
            ],
            "description": "HTML block that incorporates external content, such as interactive maps or entire web pages."
          },
          "properties": {
            "htmlData": {
              "ref": "HTMLData",
              "metadata": {
                "description": "HTML details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        }
      }
    },
    "DividerNode": {
      "metadata": {
        "section": "Divider",
        "examples": [
          "divider"
        ],
        "features": [
          "DIVIDER"
        ],
        "description": "Horizontal line between different content."
      },
      "properties": {
        "type": {
          "enum": [
            "DIVIDER"
          ],
          "metadata": {
            "description": "Node type. \n Must be `DIVIDER`."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "dividerData": {
          "ref": "DividerData",
          "metadata": {
            "description": "Divider details."
          }
        },
        "nodes": {
          "metadata": {
            "internal": true
          },
          "elements": {
            "ref": "Never"
          }
        }
      }
    },
    "DividerData": {
      "optionalProperties": {
        "containerData": {
          "ref": "PluginContainerData",
          "metadata": {
            "description": "Divider container styling."
          }
        },
        "lineStyle": {
          "enum": [
            "SINGLE",
            "DOUBLE",
            "DASHED",
            "DOTTED"
          ],
          "metadata": {
            "emitEnum": "DividerData_LineStyle",
            "description": "Divider line style."
          }
        },
        "width": {
          "enum": [
            "LARGE",
            "MEDIUM",
            "SMALL"
          ],
          "metadata": {
            "emitEnum": "DividerData_Width",
            "description": "Divider width."
          }
        },
        "alignment": {
          "enum": [
            "CENTER",
            "LEFT",
            "RIGHT"
          ],
          "metadata": {
            "emitEnum": "DividerData_Alignment",
            "description": "Divider alignment."
          }
        }
      },
      "metadata": {
        "section": "Divider"
      }
    },
    "EmbedNode": {
      "metadata": {
        "section": "Embed",
        "examples": [
          "embed"
        ],
        "features": [
          "EMBED"
        ],
        "description": "Embedded content when you include a link to a resource, without having to parse the resource directly. This provides a standardized way for web developers to include content such as videos, images, and other multimedia elements from supported providers. To learn more, see [oembed](https://oembed.com/)."
      },
      "properties": {
        "type": {
          "enum": [
            "EMBED"
          ],
          "metadata": {
            "description": "Node type. \n Must be `EMBED`."
          }
        },
        "embedData": {
          "ref": "EmbedData",
          "metadata": {
            "description": "oEmbed details."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "internal": true
          },
          "elements": {
            "ref": "Never"
          }
        }
      }
    },
    "EmbedData": {
      "optionalProperties": {
        "containerData": {
          "metadata": {
            "description": "oEmbed node container styling."
          },
          "ref": "PluginContainerData"
        },
        "oembed": {
          "metadata": {
            "description": "[oEmbed](https://www.oembed.com) details."
          },
          "ref": "Oembed"
        },
        "src": {
          "metadata": {
            "description": "Original asset source."
          },
          "type": "string"
        }
      },
      "metadata": {
        "section": "Embed"
      }
    },
    "Oembed": {
      "optionalProperties": {
        "type": {
          "metadata": {
            "description": "Resource type."
          },
          "type": "string"
        },
        "width": {
          "metadata": {
            "description": "Width of the resource specified in the `url` field in pixels."
          },
          "type": "int32"
        },
        "height": {
          "metadata": {
            "description": "Height of the resource specified in the `url` field in pixels."
          },
          "type": "int32"
        },
        "title": {
          "metadata": {
            "description": "Resource title."
          },
          "type": "string"
        },
        "url": {
          "metadata": {
            "description": "Resource source URL."
          },
          "type": "string"
        },
        "html": {
          "metadata": {
            "description": "HTML to embed a video player. The HTML should have no padding or margins."
          },
          "type": "string"
        },
        "authorName": {
          "metadata": {
            "description": "Name of the author or owner of the resource."
          },
          "type": "string"
        },
        "authorUrl": {
          "metadata": {
            "description": "URL for the author or owner of the resource."
          },
          "type": "string"
        },
        "providerName": {
          "metadata": {
            "description": "Name of the resource provider."
          },
          "type": "string"
        },
        "providerUrl": {
          "metadata": {
            "description": "URL for the resource provider."
          },
          "type": "string"
        },
        "thumbnailUrl": {
          "metadata": {
            "description": "Thumbnail image URL for the resource. If defined, `thumbnailWidth` and `thumbnailHeight` must also be defined."
          },
          "type": "string"
        },
        "thumbnailWidth": {
          "metadata": {
            "description": "Resource's thumbnail image width. If defined, `thumbnailUrl` and `thumbnailHeight` must also be defined."
          },
          "type": "string"
        },
        "thumbnailHeight": {
          "metadata": {
            "description": "Resource's thumbnail image height. If defined, `thumbnailUrl` and `thumbnailWidth`must also be defined."
          },
          "type": "string"
        },
        "videoUrl": {
          "metadata": {
            "description": "Embedded video URL."
          },
          "type": "string"
        },
        "version": {
          "metadata": {
            "description": "The oEmbed version number. Must be `1.0`."
          },
          "type": "string"
        }
      },
      "metadata": {
        "section": "Embed"
      }
    },
    "FileNode": {
      "metadata": {
        "section": "File",
        "examples": [
          "file"
        ],
        "features": [
          "FILE"
        ],
        "description": "File that is uploaded and accessed in a page. Files can be different types of content, such as text documents, images, and videos."
      },
      "properties": {
        "type": {
          "enum": [
            "FILE"
          ],
          "metadata": {
            "description": "Node type. \n Must be `FILE`."
          }
        },
        "fileData": {
          "ref": "FileData",
          "metadata": {
            "description": "File details."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "internal": true
          },
          "elements": {
            "ref": "Never"
          }
        }
      }
    },
    "FileData": {
      "optionalProperties": {
        "containerData": {
          "metadata": {
            "description": "File container styling."
          },
          "ref": "PluginContainerData"
        },
        "src": {
          "metadata": {
            "description": "File data source."
          },
          "ref": "FileSource"
        },
        "name": {
          "metadata": {
            "description": "File name."
          },
          "type": "string"
        },
        "type": {
          "metadata": {
            "description": "File type."
          },
          "type": "string"
        },
        "size": {
          "metadata": {
            "description": "Deprecated. Replaced by `sizeInKb`",
            "deprecated": true
          },
          "type": "uint32"
        },
        "sizeInKb": {
          "metadata": {
            "description": "File size in KB."
          },
          "type": "string"
        },
        "pdfSettings": {
          "metadata": {
            "description": "Settings for PDF files."
          },
          "ref": "FileData_PDFSettings"
        },
        "mimeType": {
          "metadata": {
            "description": "File MIME type."
          },
          "type": "string"
        },
        "path": {
          "metadata": {
            "description": "File path."
          },
          "type": "string"
        }
      },
      "metadata": {
        "section": "File"
      }
    },
    "FileData_PDFSettings": {
      "optionalProperties": {
        "viewMode": {
          "metadata": {
            "emitEnum": "FileData_PDFSettings_ViewMode",
            "description": "PDF view mode. Supported values: \n - `NONE`: PDF isn't displayed. \n - `FULL`: Full PDF page view is displayed. \n - `MINI`: Mini view of the PDF is displayed."
          },
          "enum": [
            "NONE",
            "FULL",
            "MINI"
          ]
        },
        "disableDownload": {
          "metadata": {
            "description": "Whether the PDF download button is disabled. \n Default: `false`"
          },
          "type": "boolean"
        },
        "disablePrint": {
          "metadata": {
            "description": "Whether the PDF print button is disabled. \n Default: `false`"
          },
          "type": "boolean"
        }
      },
      "metadata": {
        "section": "File"
      }
    },
    "GalleryNode": {
      "metadata": {
        "section": "Gallery",
        "examples": [
          "gallery"
        ],
        "features": [
          "GALLERY"
        ],
        "description": "Collection of images displayed together."
      },
      "properties": {
        "type": {
          "enum": [
            "GALLERY"
          ],
          "metadata": {
            "description": "Node type. \n Must be `GALLERY`."
          }
        },
        "galleryData": {
          "ref": "GalleryData",
          "metadata": {
            "description": "Gallery node details."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "internal": true
          },
          "elements": {
            "ref": "Never"
          }
        }
      }
    },
    "GalleryData": {
      "properties": {
        "items": {
          "elements": {
            "ref": "GalleryData_Item"
          },
          "metadata": {
            "description": "Gallery items details.",
            "validation": {
              "minSize": 1
            }
          }
        }
      },
      "optionalProperties": {
        "containerData": {
          "ref": "PluginContainerData",
          "metadata": {
            "description": "Gallery container styling."
          }
        },
        "options": {
          "ref": "GalleryOptions",
          "metadata": {
            "description": "Gallery appearance options."
          }
        },
        "disableExpand": {
          "type": "boolean",
          "metadata": {
            "description": "Whether the expand button is disabled. \nDefault: `false`"
          }
        },
        "disableDownload": {
          "type": "boolean",
          "metadata": {
            "description": "Whether the download button is disabled. \nDefault: `false`"
          }
        }
      },
      "metadata": {
        "section": "Gallery"
      }
    },
    "GalleryOptions": {
      "optionalProperties": {
        "layout": {
          "ref": "GalleryOptions_Layout",
          "metadata": {
            "description": "Gallery layout."
          }
        },
        "item": {
          "ref": "GalleryOptions_ItemStyle",
          "metadata": {
            "description": "Styling for gallery items."
          }
        },
        "thumbnails": {
          "ref": "GalleryOptions_Thumbnails",
          "metadata": {
            "description": "Styling for gallery thumbnail images."
          }
        }
      },
      "metadata": {
        "section": "Gallery"
      }
    },
    "GalleryOptions_Layout": {
      "optionalProperties": {
        "type": {
          "metadata": {
            "emitEnum": "GalleryOptions_Layout_Type",
            "description": "Gallery layout. Supported values: <br> - `COLLAGE`: Presents images in various sizes and positions, creating an artistic or dynamic layout. <br> - `MASONRY`: Grid-based design that places items in optimal positions based on available vertical space, creating an arrangement where items of varying heights fit together like bricks in a wall. <br> - `GRID`: A layout structure that organizes items into rows and columns, creating a uniform arrangement of images. <br> - `THUMBNAIL`: Smaller, scaled-down version of an image that serves as a preview. <br> - `SLIDER`: Component that allows users to browse through a set of images by sliding or swiping through them, typically in a confined viewing area. <br> - `SLIDESHOW`: A component that automatically or manually transitions through a set of images in a designated area. <br> - `PANORAMA`: Wide-angle or 360-degree image that allows users to view an extended horizontal view of a scene. <br> - `COLUMN`: Arrangement of the gallery items in vertical sections. <br> - `MAGIC`: Visually stunning, dynamic effects that enhance the user experience. <br> - `FULLSIZE`: A layout where each image or item is displayed at its full size, sometimes  taking up the entire viewport or a significant portion of the screen."
          },
          "enum": [
            "COLLAGE",
            "MASONRY",
            "GRID",
            "THUMBNAIL",
            "SLIDER",
            "SLIDESHOW",
            "PANORAMA",
            "COLUMN",
            "MAGIC",
            "FULLSIZE"
          ]
        },
        "horizontalScroll": {
          "type": "boolean",
          "metadata": {
            "description": "Whether the horizontal scroll is enabled. \nDefault: `true`, unless the `type` field is set to `GRID` or `COLLAGE`."
          }
        },
        "orientation": {
          "metadata": {
            "description": "Gallery orientation.",
            "emitEnum": "GalleryOptions_Layout_Orientation"
          },
          "enum": [
            "ROWS",
            "COLUMNS"
          ]
        },
        "numberOfColumns": {
          "type": "int32",
          "metadata": {
            "description": "Number of columns to display on computer screens."
          }
        },
        "mobileNumberOfColumns": {
          "type": "int32",
          "metadata": {
            "description": "Number of columns to display on mobile phone screens."
          }
        }
      },
      "metadata": {
        "section": "Gallery"
      }
    },
    "GalleryOptions_ItemStyle": {
      "optionalProperties": {
        "targetSize": {
          "type": "int32",
          "metadata": {
            "description": "Dimensions for each gallery item in pixels. Behavior may change with gallery type."
          }
        },
        "ratio": {
          "type": "float64",
          "metadata": {
            "description": "Height to width ratio."
          }
        },
        "crop": {
          "metadata": {
            "description": "Cropping strategy. Supported values: <br> - `FILL`: Each item in the gallery is cropped and resized to completely fill its container. <br> - `FIT`: Each item in the gallery is resized to fit exactly in its container without cropping.",
            "emitEnum": "GalleryOptions_ItemStyle_Crop"
          },
          "enum": [
            "FILL",
            "FIT"
          ]
        },
        "spacing": {
          "type": "int32",
          "metadata": {
            "description": "Spacing between gallery items in pixels."
          }
        }
      },
      "metadata": {
        "section": "Gallery"
      }
    },
    "GalleryOptions_Thumbnails": {
      "optionalProperties": {
        "placement": {
          "metadata": {
            "emitEnum": "GalleryOptions_Thumbnails_Alignment",
            "description": "Thumbnail alignment."
          },
          "enum": [
            "TOP",
            "RIGHT",
            "BOTTOM",
            "LEFT",
            "NONE"
          ]
        },
        "spacing": {
          "type": "int32",
          "metadata": {
            "description": "Spacing between thumbnails in pixels."
          }
        }
      },
      "metadata": {
        "section": "Gallery"
      }
    },
    "GalleryData_Item": {
      "metadata": {
        "section": "Gallery",
        "oneOf": {
          "strict": true,
          "properties": [
            "image",
            "video"
          ]
        }
      },
      "optionalProperties": {
        "title": {
          "type": "string",
          "metadata": {
            "description": "Item title."
          }
        },
        "altText": {
          "type": "string",
          "metadata": {
            "description": "Item's alternative text."
          }
        },
        "image": {
          "ref": "GalleryData_Item_Image",
          "metadata": {
            "description": "Image item."
          }
        },
        "video": {
          "ref": "GalleryData_Item_Video",
          "metadata": {
            "description": "Video item."
          }
        }
      }
    },
    "GalleryData_Item_Video": {
      "properties": {
        "media": {
          "ref": "Media",
          "metadata": {
            "description": "Video file details."
          }
        }
      },
      "optionalProperties": {
        "thumbnail": {
          "ref": "Media",
          "metadata": {
            "description": "Video thumbnail file details."
          }
        }
      },
      "metadata": {
        "section": "Gallery"
      }
    },
    "GalleryData_Item_Image": {
      "properties": {
        "media": {
          "ref": "Media",
          "metadata": {
            "description": "Image file details."
          }
        }
      },
      "optionalProperties": {
        "link": {
          "ref": "Link",
          "metadata": {
            "description": "Link details for images, if relevant."
          }
        }
      },
      "metadata": {
        "section": "Gallery"
      }
    },
    "GifNode": {
      "metadata": {
        "section": "Gif",
        "examples": [
          "gif-basic"
        ],
        "features": [
          "GIF"
        ],
        "description": "GIF images."
      },
      "properties": {
        "type": {
          "enum": [
            "GIF"
          ],
          "metadata": {
            "description": "Node type. \n Must be `GIF`."
          }
        },
        "gifData": {
          "ref": "GIFData",
          "metadata": {
            "description": "GIF details."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "description": "Child nodes to define a GIF caption."
          },
          "elements": {
            "ref": "CaptionNode"
          }
        }
      }
    },
    "GIFData": {
      "optionalProperties": {
        "containerData": {
          "ref": "PluginContainerData",
          "metadata": {
            "description": "GIF container styling."
          }
        },
        "original": {
          "metadata": {
            "description": "Source for the full size GIF."
          },
          "ref": "GIF"
        },
        "downsized": {
          "metadata": {
            "description": "Source for the downsized GIF."
          },
          "ref": "GIF"
        },
        "height": {
          "type": "int32",
          "metadata": {
            "description": "Height in pixels."
          }
        },
        "width": {
          "type": "int32",
          "metadata": {
            "description": "Width in pixels."
          }
        },
        "gifType": {
          "enum": [
            "NORMAL",
            "STICKER"
          ],
          "metadata": {
            "emitEnum": "GIFData_GIFType",
            "description": "Type of GIF (Sticker or NORMAL). \n Default: `NORMAL`"
          }
        }
      },
      "metadata": {
        "section": "Gif"
      }
    },
    "GIF": {
      "optionalProperties": {
        "gif": {
          "type": "string",
          "metadata": {
            "format": "WEB_URL",
            "description": "GIF source URL."
          }
        },
        "mp4": {
          "type": "string",
          "metadata": {
            "format": "WEB_URL",
            "description": "MP4 source URL."
          }
        },
        "still": {
          "type": "string",
          "metadata": {
            "format": "WEB_URL",
            "description": "GIF thumbnail URL."
          }
        }
      },
      "metadata": {
        "section": "Gif"
      }
    },
    "HtmlNode": {
      "metadata": {
        "section": "Html",
        "examples": [
          "html-code",
          "html-url"
        ],
        "features": [
          "HTML"
        ],
        "description": "HTML block that incorporates external content, such as interactive maps or entire web pages."
      },
      "properties": {
        "type": {
          "enum": [
            "HTML"
          ],
          "metadata": {
            "description": "Node type. \n Mst be `HTML`."
          }
        },
        "htmlData": {
          "ref": "HTMLData",
          "metadata": {
            "description": "HTML details."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "internal": true
          },
          "elements": {
            "ref": "Never"
          }
        }
      }
    },
    "HTMLData": {
      "metadata": {
        "section": "Html",
        "oneOf": {
          "properties": [
            "url",
            "html"
          ]
        }
      },
      "optionalProperties": {
        "containerData": {
          "metadata": {
            "description": "HTML node container styling. Height property is irrelevant for HTML embeds when autoHeight is set to `true`."
          },
          "ref": "PluginContainerData"
        },
        "source": {
          "metadata": {
            "description": "HTML code type. Possible values: \n - `HTML`: HTML code. \n - [`ADSENSE`](https://adsense.google.com/start/): Service provided by Google for rendering advertisements on web pages.",
            "emitEnum": "HTMLData_Source"
          },
          "enum": [
            "HTML",
            "ADSENSE"
          ]
        },
        "url": {
          "metadata": {
            "description": "HTML code URL."
          },
          "type": "string"
        },
        "html": {
          "metadata": {
            "description": "HTML code."
          },
          "type": "string"
        },
        "autoHeight": {
          "metadata": {
            "description": "If container height is aligned with its content height. \n Default: `true`"
          },
          "type": "boolean"
        }
      }
    },
    "ImageNode": {
      "metadata": {
        "section": "Image",
        "examples": [
          "image-id",
          "image-url"
        ],
        "features": [
          "IMAGE"
        ],
        "description": "Standalone images in a page."
      },
      "properties": {
        "type": {
          "enum": [
            "IMAGE"
          ],
          "metadata": {
            "description": "Node type. \n Must be `IMAGE`."
          }
        },
        "imageData": {
          "ref": "ImageData",
          "metadata": {
            "description": "Image options."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "description": "Child nodes to define an image caption."
          },
          "elements": {
            "ref": "CaptionNode"
          }
        }
      }
    },
    "ImageData": {
      "properties": {
        "image": {
          "ref": "Media",
          "metadata": {
            "description": "Image file details."
          }
        }
      },
      "optionalProperties": {
        "containerData": {
          "ref": "PluginContainerData",
          "metadata": {
            "description": "Image container styling."
          }
        },
        "link": {
          "ref": "Link",
          "metadata": {
            "description": "Image link details."
          }
        },
        "disableExpand": {
          "type": "boolean",
          "metadata": {
            "description": "Whether the image expands to the full screen when clicked. \n Default: `false`"
          }
        },
        "altText": {
          "type": "string",
          "metadata": {
            "description": "Alternate text to display when the image doesn't load."
          }
        },
        "caption": {
          "type": "string",
          "metadata": {
            "description": "Deprecated. Use `CaptionNode` instead.",
            "deprecated": true
          }
        },
        "disableDownload": {
          "type": "boolean",
          "metadata": {
            "description": "Whether the download image button is disabled. \n Default: `false`"
          }
        },
        "decorative": {
          "type": "boolean",
          "metadata": {
            "description": "Whether the image is decorative and does not need an explanation. Defaults to `false`."
          }
        },
        "styles": {
          "ref": "ImageData_Styles",
          "metadata": {
            "description": "Image styling."
          }
        }
      },
      "metadata": {
        "section": "Image"
      }
    },
    "ImageData_Styles": {
      "optionalProperties": {
        "border": {
          "ref": "ImageData_Styles_Border",
          "metadata": {
            "description": "Border properties."
          }
        }
      },
      "metadata": {
        "section": "Image"
      }
    },
    "ImageData_Styles_Border": {
      "optionalProperties": {
        "width": {
          "type": "int32",
          "metadata": {
            "description": "Border width in pixels."
          }
        },
        "color": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Border color as a hexadecimal value."
          }
        },
        "radius": {
          "type": "int32",
          "metadata": {
            "description": "Border radius in pixels."
          }
        }
      },
      "metadata": {
        "section": "Image"
      }
    },
    "LinkPreviewNode": {
      "metadata": {
        "section": "Link Preview",
        "examples": [
          "link-preview"
        ],
        "features": [
          "LINK_PREVIEW"
        ],
        "description": "Preview or summary of a webpage. Link previews include elements such as the title of the linked page, a brief description or excerpt of the content, and sometimes a thumbnail image or other relevant information."
      },
      "properties": {
        "type": {
          "enum": [
            "LINK_PREVIEW"
          ],
          "metadata": {
            "description": "Node type. \n Must be `LINK_PREVIEW`."
          }
        },
        "linkPreviewData": {
          "ref": "LinkPreviewData",
          "metadata": {
            "description": "Link preview details."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "internal": true
          },
          "elements": {
            "ref": "Never"
          }
        }
      }
    },
    "LinkPreviewData": {
      "optionalProperties": {
        "containerData": {
          "ref": "PluginContainerData",
          "metadata": {
            "description": "Link preview container styling."
          }
        },
        "link": {
          "ref": "Link",
          "metadata": {
            "description": "Link details."
          }
        },
        "title": {
          "type": "string",
          "metadata": {
            "description": "Preview title."
          }
        },
        "thumbnailUrl": {
          "type": "string",
          "metadata": {
            "description": "Preview thumbnail URL."
          }
        },
        "description": {
          "type": "string",
          "metadata": {
            "description": "Preview description."
          }
        },
        "html": {
          "type": "string",
          "metadata": {
            "description": "Preview content as HTML."
          }
        },
        "styles": {
          "ref": "LinkPreviewData_Styles",
          "metadata": {
            "description": "Styling for the link preview."
          }
        }
      },
      "metadata": {
        "section": "Link Preview"
      }
    },
    "LinkPreviewData_Styles": {
      "optionalProperties": {
        "backgroundColor": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Background color as a hexadecimal value."
          }
        },
        "titleColor": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Title color as a hexadecimal value."
          }
        },
        "subtitleColor": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Subtitle color as a hexadecimal value."
          }
        },
        "linkColor": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Link color as a hexadecimal value."
          }
        },
        "borderWidth": {
          "type": "int32",
          "metadata": {
            "description": "Border width in pixels."
          }
        },
        "borderRadius": {
          "type": "int32",
          "metadata": {
            "description": "Border radius in pixels."
          }
        },
        "borderColor": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Border color as a hexadecimal value."
          }
        },
        "thumbnailPosition": {
          "enum": [
            "START",
            "END",
            "TOP",
            "HIDDEN"
          ],
          "metadata": {
            "description": "Position of thumbnail. Defaults to `START`."
          }
        }
      },
      "metadata": {
        "section": "Link Preview"
      }
    },
    "PollNode": {
      "metadata": {
        "section": "Poll",
        "examples": [
          "poll"
        ],
        "features": [
          "POLL"
        ],
        "description": "Provides a question with several options, where the options' cumulative total may be shown."
      },
      "properties": {
        "type": {
          "enum": [
            "POLL"
          ],
          "metadata": {
            "description": "Node type. \n Must be `POLL`."
          }
        },
        "pollData": {
          "ref": "PollData",
          "metadata": {
            "description": "Poll details."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "internal": true
          },
          "elements": {
            "ref": "Never"
          }
        }
      }
    },
    "PollData": {
      "optionalProperties": {
        "containerData": {
          "ref": "PluginContainerData",
          "metadata": {
            "description": "Poll container styling."
          }
        },
        "poll": {
          "ref": "PollData_Poll",
          "metadata": {
            "description": "Poll data."
          }
        },
        "layout": {
          "ref": "PollData_Layout",
          "metadata": {
            "description": "Poll layout settings."
          }
        },
        "design": {
          "ref": "PollData_Design",
          "metadata": {
            "description": "Poll design."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Poll": {
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "title": {
          "type": "string",
          "metadata": {
            "description": "Poll title."
          }
        },
        "creatorId": {
          "type": "string",
          "metadata": {
            "description": "Poll creator ID."
          }
        },
        "image": {
          "ref": "Media",
          "metadata": {
            "description": "Main poll image."
          }
        },
        "options": {
          "elements": {
            "ref": "PollData_Poll_Option"
          },
          "metadata": {
            "description": "Poll details."
          }
        },
        "settings": {
          "ref": "PollData_Poll_Settings",
          "metadata": {
            "description": "Poll display settings."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Poll_Option": {
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "description": "Poll option ID."
          }
        },
        "title": {
          "type": "string",
          "metadata": {
            "description": "Poll option title"
          }
        },
        "image": {
          "ref": "Media",
          "metadata": {
            "description": "Image displayed with the option."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Poll_Settings": {
      "optionalProperties": {
        "permissions": {
          "ref": "PollData_Poll_Settings_Permissions",
          "metadata": {
            "description": "Voting permission settings."
          }
        },
        "showVoters": {
          "type": "boolean",
          "metadata": {
            "description": "Whether to display the voters in the vote results. \n Default: `true`."
          }
        },
        "showVotesCount": {
          "type": "boolean",
          "metadata": {
            "description": "Whether to display the vote count. \n Default: `true`."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Poll_Settings_Permissions": {
      "optionalProperties": {
        "view": {
          "metadata": {
            "emitEnum": "PollData_Poll_Settings_Permissions_ViewRole",
            "description": "Who can view the poll results."
          },
          "enum": [
            "CREATOR",
            "VOTERS",
            "EVERYONE"
          ]
        },
        "vote": {
          "metadata": {
            "description": "Who can vote.",
            "emitEnum": "PollData_Poll_Settings_Permissions_VoteRole"
          },
          "enum": [
            "SITE_MEMBERS",
            "ALL"
          ]
        },
        "allowMultipleVotes": {
          "type": "boolean",
          "metadata": {
            "description": "Whether a user can vote multiple times. \n Default: `false`"
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Layout": {
      "optionalProperties": {
        "poll": {
          "ref": "PollData_Layout_PollLayout",
          "metadata": {
            "description": "Poll layout settings."
          }
        },
        "options": {
          "ref": "PollData_Layout_OptionLayout",
          "metadata": {
            "description": "Poll options layout settings."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Layout_PollLayout": {
      "optionalProperties": {
        "type": {
          "enum": [
            "LIST",
            "GRID"
          ],
          "metadata": {
            "emitEnum": "PollData_Layout_PollLayout_Type",
            "description": "Voting options layout."
          }
        },
        "direction": {
          "enum": [
            "LTR",
            "RTL"
          ],
          "metadata": {
            "emitEnum": "PollData_Layout_PollLayout_Direction",
            "description": "Direction of the text in the poll. Supported values: <br> - `LTR`: Left to right. <br> - `RTL`: Right to left."
          }
        },
        "enableImage": {
          "type": "boolean",
          "metadata": {
            "description": "Whether to display the main poll image. \n Default: `false`"
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Layout_OptionLayout": {
      "optionalProperties": {
        "enableImage": {
          "type": "boolean",
          "metadata": {
            "description": "Whether to display option images. \n Defaults: `false`."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Design": {
      "optionalProperties": {
        "poll": {
          "ref": "PollData_Design_PollDesign",
          "metadata": {
            "description": "Poll styling."
          }
        },
        "options": {
          "ref": "PollData_Design_OptionDesign",
          "metadata": {
            "description": "Voting options styling."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Design_PollDesign": {
      "optionalProperties": {
        "background": {
          "ref": "PollData_Design_PollDesign_Background",
          "metadata": {
            "description": "Poll background styling."
          }
        },
        "borderRadius": {
          "type": "int32",
          "metadata": {
            "description": "Border radius in pixels."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Design_PollDesign_Background": {
      "discriminator": "type",
      "metadata": {
        "section": "Poll",
        "refUnion": {
          "COLOR": "PollData_Background_ColorVariant",
          "IMAGE": "PollData_Background_ImageVariant",
          "GRADIENT": "PollData_Background_GradientVariant"
        },
        "emitEnum": "PollData_Design_PollDesign_Background_Type"
      },
      "mapping": {
        "COLOR": {
          "properties": {},
          "optionalProperties": {
            "color": {
              "type": "string",
              "metadata": {
                "format": "COLOR_HEX",
                "description": "Background color as a hexademical value."
              }
            }
          },
          "metadata": {
            "section": "Poll"
          }
        },
        "IMAGE": {
          "properties": {},
          "optionalProperties": {
            "image": {
              "ref": "Media",
              "metadata": {
                "description": "Image to use for the background."
              }
            }
          },
          "metadata": {
            "section": "Poll"
          }
        },
        "GRADIENT": {
          "properties": {},
          "optionalProperties": {
            "gradient": {
              "ref": "PollData_Design_PollDesign_Background_Gradient",
              "metadata": {
                "description": "Gradient background details."
              }
            }
          },
          "metadata": {
            "section": "Poll"
          }
        }
      }
    },
    "PollData_Background_ColorVariant": {
      "properties": {
        "type": {
          "enum": [
            "COLOR"
          ]
        }
      },
      "optionalProperties": {
        "color": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Background color as a hexademical value."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Background_ImageVariant": {
      "properties": {
        "type": {
          "enum": [
            "IMAGE"
          ]
        }
      },
      "optionalProperties": {
        "image": {
          "ref": "Media",
          "metadata": {
            "description": "Image to use for the background."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Background_GradientVariant": {
      "properties": {
        "type": {
          "enum": [
            "GRADIENT"
          ]
        }
      },
      "optionalProperties": {
        "gradient": {
          "ref": "PollData_Design_PollDesign_Background_Gradient",
          "metadata": {
            "description": "Gradient background details."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Design_PollDesign_Background_Gradient": {
      "optionalProperties": {
        "angle": {
          "type": "int32",
          "metadata": {
            "description": "Gradient angle in degrees."
          }
        },
        "startColor": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Start color as a hexadecimal value."
          }
        },
        "lastColor": {
          "type": "string",
          "metadata": {
            "format": "COLOR_HEX",
            "description": "End color as a hexadecimal value."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "PollData_Design_OptionDesign": {
      "optionalProperties": {
        "borderRadius": {
          "type": "int32",
          "metadata": {
            "description": "Border radius in pixels."
          }
        }
      },
      "metadata": {
        "section": "Poll"
      }
    },
    "TableNode": {
      "metadata": {
        "section": "Table",
        "examples": [
          "table"
        ],
        "features": [
          "TABLE"
        ],
        "description": "Data in rows and columns."
      },
      "properties": {
        "type": {
          "enum": [
            "TABLE"
          ],
          "metadata": {
            "description": "Node type. \n Must be `TABLE`."
          }
        },
        "nodes": {
          "elements": {
            "ref": "TableRowNode"
          },
          "metadata": {
            "description": "Child nodes to define table rows.",
            "validation": {
              "minSize": 1
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "tableData": {
          "ref": "TableData",
          "metadata": {
            "description": "Table details."
          }
        }
      }
    },
    "TableRowNode": {
      "properties": {
        "type": {
          "enum": [
            "TABLE_ROW"
          ],
          "metadata": {
            "description": "Node type. \n Must be `TABLE_ROW`."
          }
        },
        "nodes": {
          "elements": {
            "ref": "TableCellNode"
          },
          "metadata": {
            "description": "Child nodes to define table cells.",
            "validation": {
              "minSize": 1
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        }
      },
      "metadata": {
        "section": "Table"
      }
    },
    "TableData": {
      "optionalProperties": {
        "containerData": {
          "metadata": {
            "description": "Table container styling."
          },
          "ref": "PluginContainerData"
        },
        "dimensions": {
          "metadata": {
            "description": "Table dimensions."
          },
          "ref": "TableData_Dimensions"
        },
        "rowHeader": {
          "metadata": {
            "description": "Whether the table's first row is a heading. <br> Default: `false`"
          },
          "type": "boolean"
        },
        "columnHeader": {
          "metadata": {
            "description": "Whether the table's first column is a heading. <br> Default: `false`"
          },
          "type": "boolean"
        }
      },
      "metadata": {
        "section": "Table"
      }
    },
    "TableData_Dimensions": {
      "optionalProperties": {
        "colsWidthRatio": {
          "elements": {
            "type": "float64"
          },
          "metadata": {
            "description": "Each column width as a fraction to the width of table."
          }
        },
        "rowsHeight": {
          "elements": {
            "type": "uint32"
          },
          "metadata": {
            "description": "Height of each row."
          }
        },
        "colsMinWidth": {
          "elements": {
            "type": "uint32"
          },
          "metadata": {
            "description": "Minimum width of each column."
          }
        }
      },
      "metadata": {
        "section": "Table"
      }
    },
    "TableCellNode": {
      "properties": {
        "type": {
          "enum": [
            "TABLE_CELL"
          ],
          "metadata": {
            "description": "Node type. \n Must be `TABLE_CELL`."
          }
        },
        "nodes": {
          "elements": {
            "ref": "TableCellChildNode"
          },
          "metadata": {
            "description": "Child nodes to define cell content.",
            "validation": {
              "minSize": 1
            }
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "tableCellData": {
          "ref": "TableCellData",
          "metadata": {
            "description": "Table cell details."
          }
        }
      },
      "metadata": {
        "section": "Table"
      }
    },
    "TableCellChildNode": {
      "discriminator": "type",
      "metadata": {
        "section": "Table",
        "refUnion": {
          "PARAGRAPH": "ParagraphNode",
          "HEADING": "HeadingNode",
          "BULLETED_LIST": "BulletedListNode",
          "ORDERED_LIST": "OrderedListNode",
          "BLOCKQUOTE": "BlockquoteNode",
          "AUDIO": "AudioNode",
          "FILE": "FileNode",
          "IMAGE": "ImageNode",
          "VIDEO": "VideoNode",
          "GIF": "GifNode",
          "DIVIDER": "DividerNode",
          "CODE_BLOCK": "CodeBlockNode",
          "HTML": "HtmlNode",
          "LINK_PREVIEW": "LinkPreviewNode",
          "APP_EMBED": "AppEmbedNode",
          "BUTTON": "ButtonNode",
          "EMBED": "EmbedNode"
        },
        "description": "Table cell content node."
      },
      "mapping": {
        "PARAGRAPH": {
          "metadata": {
            "section": "Paragraph",
            "description": "Structures text content, providing a clear separation between different ideas or sections of text. Paragraphs are rendered with default styling that typically includes some space above and below the paragraph, making it visually distinct from the surrounding content.",
            "examples": [
              "paragraph"
            ]
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Paragraph children."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Vertical padding. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "paragraphData": {
              "ref": "ParagraphData",
              "metadata": {
                "description": "Paragraph node attributes."
              }
            }
          }
        },
        "HEADING": {
          "metadata": {
            "section": "Heading",
            "examples": [
              "headings-all-sizes"
            ],
            "description": "Indicates a heading or title of a section. There are 6 levels of headings, with `1` being the most prominent and `6` being the least."
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Child nodes to define the heading text."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Heading styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "headingData": {
              "ref": "HeadingData",
              "metadata": {
                "description": "Heading options."
              }
            }
          }
        },
        "BULLETED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "bulletedList"
            ],
            "description": "Bulleted list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "bulletedListData": {
              "ref": "BulletedListData",
              "metadata": {
                "description": "Bulleted list details."
              }
            }
          }
        },
        "ORDERED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "orderedList"
            ],
            "description": "Numbered list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "orderedListData": {
              "ref": "OrderedListData",
              "metadata": {
                "description": "Ordered list details."
              }
            }
          }
        },
        "BLOCKQUOTE": {
          "metadata": {
            "section": "Block quote",
            "examples": [
              "blockquote"
            ],
            "description": "Used to visually distinguish quoted or cited content from the surrounding text. When rendered, it displays as the quoted text with indentation or other styling to visually separate it from the surrounding content."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ParagraphNode"
              },
              "metadata": {
                "description": "Child nodes to define a blockquote paragraph.",
                "validation": {
                  "minSize": 1,
                  "maxSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "blockquoteData": {
              "ref": "BlockquoteData",
              "metadata": {
                "description": "Blockquote details."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Defines the blockquote style, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            }
          }
        },
        "AUDIO": {
          "metadata": {
            "section": "Audio",
            "examples": [
              "audio-soundcloud"
            ],
            "features": [
              "AUDIO"
            ],
            "description": "Embeds and plays audio content directly in a webpage. Users can interact with the controls to play, pause, rewind, and adjust the volume of the audio."
          },
          "properties": {
            "audioData": {
              "ref": "AudioData",
              "metadata": {
                "description": "Audio details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "FILE": {
          "metadata": {
            "section": "File",
            "examples": [
              "file"
            ],
            "features": [
              "FILE"
            ],
            "description": "File that is uploaded and accessed in a page. Files can be different types of content, such as text documents, images, and videos."
          },
          "properties": {
            "fileData": {
              "ref": "FileData",
              "metadata": {
                "description": "File details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "IMAGE": {
          "metadata": {
            "section": "Image",
            "examples": [
              "image-id",
              "image-url"
            ],
            "features": [
              "IMAGE"
            ],
            "description": "Standalone images in a page."
          },
          "properties": {
            "imageData": {
              "ref": "ImageData",
              "metadata": {
                "description": "Image options."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define an image caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "VIDEO": {
          "metadata": {
            "section": "Video",
            "examples": [
              "video-url",
              "video-wix"
            ],
            "features": [
              "VIDEO"
            ],
            "description": "Embeds and plays video content directly in a page. When rendered, it is displayed as the video player with the specified video file. Users can interact with the controls to play, pause, rewind, and adjust the volume of the video."
          },
          "properties": {
            "videoData": {
              "ref": "VideoData",
              "metadata": {
                "description": "Video details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define a video caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "GIF": {
          "metadata": {
            "section": "Gif",
            "examples": [
              "gif-basic"
            ],
            "features": [
              "GIF"
            ],
            "description": "GIF images."
          },
          "properties": {
            "gifData": {
              "ref": "GIFData",
              "metadata": {
                "description": "GIF details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define a GIF caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "DIVIDER": {
          "metadata": {
            "section": "Divider",
            "examples": [
              "divider"
            ],
            "features": [
              "DIVIDER"
            ],
            "description": "Horizontal line between different content."
          },
          "properties": {},
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "dividerData": {
              "ref": "DividerData",
              "metadata": {
                "description": "Divider details."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "CODE_BLOCK": {
          "metadata": {
            "section": "Codeblock",
            "features": [
              "CODE_BLOCK"
            ],
            "examples": [
              "code-block-multi-line",
              "code-block-syntax-highlighting"
            ],
            "description": "Presents code snippets, markup, or other text-based content that should be displayed exactly as it appears, without any interpretation or formatting applied by the browser. When rendered, it is displayed as the code block with a monospaced font, preserving indentation, line breaks, and spacing as specified in the code."
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Child nodes to define a code block text."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "codeBlockData": {
              "ref": "CodeBlockData",
              "metadata": {
                "description": "Code block details."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Code block styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            }
          }
        },
        "HTML": {
          "metadata": {
            "section": "Html",
            "examples": [
              "html-code",
              "html-url"
            ],
            "features": [
              "HTML"
            ],
            "description": "HTML block that incorporates external content, such as interactive maps or entire web pages."
          },
          "properties": {
            "htmlData": {
              "ref": "HTMLData",
              "metadata": {
                "description": "HTML details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "LINK_PREVIEW": {
          "metadata": {
            "section": "Link Preview",
            "examples": [
              "link-preview"
            ],
            "features": [
              "LINK_PREVIEW"
            ],
            "description": "Preview or summary of a webpage. Link previews include elements such as the title of the linked page, a brief description or excerpt of the content, and sometimes a thumbnail image or other relevant information."
          },
          "properties": {
            "linkPreviewData": {
              "ref": "LinkPreviewData",
              "metadata": {
                "description": "Link preview details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "APP_EMBED": {
          "metadata": {
            "section": "App Embed",
            "examples": [
              "app-embed-event",
              "app-embed-booking"
            ],
            "features": [
              "APP_EMBED"
            ],
            "description": "Embeds an external content preview from Wix business solutions. For example, you can embed previews from the Wix Bookings, Wix Stores and Wix Events apps."
          },
          "properties": {
            "appEmbedData": {
              "ref": "AppEmbedData",
              "metadata": {
                "description": "Embedded Wix app."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "BUTTON": {
          "metadata": {
            "section": "Button",
            "examples": [
              "button-link",
              "button-action",
              "button-styled"
            ],
            "features": [
              "BUTTON"
            ],
            "description": "Button to trigger an action."
          },
          "properties": {
            "buttonData": {
              "ref": "ButtonData",
              "metadata": {
                "description": "Button details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "EMBED": {
          "metadata": {
            "section": "Embed",
            "examples": [
              "embed"
            ],
            "features": [
              "EMBED"
            ],
            "description": "Embedded content when you include a link to a resource, without having to parse the resource directly. This provides a standardized way for web developers to include content such as videos, images, and other multimedia elements from supported providers. To learn more, see [oembed](https://oembed.com/)."
          },
          "properties": {
            "embedData": {
              "ref": "EmbedData",
              "metadata": {
                "description": "oEmbed details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        }
      }
    },
    "TableCellData": {
      "optionalProperties": {
        "cellStyle": {
          "metadata": {
            "description": "Cell styling."
          },
          "ref": "TableCellData_CellStyle"
        },
        "borderColors": {
          "metadata": {
            "description": "Cell border colors."
          },
          "ref": "TableCellData_BorderColors"
        }
      },
      "metadata": {
        "section": "Table"
      }
    },
    "TableCellData_CellStyle": {
      "optionalProperties": {
        "verticalAlignment": {
          "metadata": {
            "description": "Vertical alignment for the cell's text.",
            "emitEnum": "TableCellData_VerticalAlignment"
          },
          "enum": [
            "TOP",
            "MIDDLE",
            "BOTTOM"
          ]
        },
        "backgroundColor": {
          "metadata": {
            "description": "Cell background color as a hexadecimal value.",
            "format": "COLOR_HEX"
          },
          "type": "string"
        }
      },
      "metadata": {
        "section": "Table"
      }
    },
    "TableCellData_BorderColors": {
      "optionalProperties": {
        "left": {
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Left border color as a hexadecimal value."
          },
          "type": "string"
        },
        "right": {
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Right border color as a hexadecimal value."
          },
          "type": "string"
        },
        "top": {
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Top border color as a hexadecimal value."
          },
          "type": "string"
        },
        "bottom": {
          "metadata": {
            "format": "COLOR_HEX",
            "description": "Bottom border color as a hexadecimal value."
          },
          "type": "string"
        }
      },
      "metadata": {
        "section": "Table"
      }
    },
    "VideoNode": {
      "metadata": {
        "section": "Video",
        "examples": [
          "video-url",
          "video-wix"
        ],
        "features": [
          "VIDEO"
        ],
        "description": "Embeds and plays video content directly in a page. When rendered, it is displayed as the video player with the specified video file. Users can interact with the controls to play, pause, rewind, and adjust the volume of the video."
      },
      "properties": {
        "type": {
          "enum": [
            "VIDEO"
          ],
          "metadata": {
            "description": "Node type. \n Must be `VIDEO`."
          }
        },
        "videoData": {
          "ref": "VideoData",
          "metadata": {
            "description": "Video details."
          }
        }
      },
      "optionalProperties": {
        "id": {
          "type": "string",
          "metadata": {
            "format": "NODE_ID",
            "description": "[Node ID](#node-ids)."
          }
        },
        "nodes": {
          "metadata": {
            "description": "Child nodes to define a video caption."
          },
          "elements": {
            "ref": "CaptionNode"
          }
        }
      }
    },
    "VideoData": {
      "properties": {
        "video": {
          "metadata": {
            "description": "Video file details."
          },
          "ref": "Media"
        }
      },
      "optionalProperties": {
        "containerData": {
          "metadata": {
            "description": "Video container styling."
          },
          "ref": "PluginContainerData"
        },
        "thumbnail": {
          "metadata": {
            "description": "Video thumbnail details."
          },
          "ref": "Media"
        },
        "disableDownload": {
          "metadata": {
            "description": "Whether the video download button is disabled. \n Default: `false`"
          },
          "type": "boolean"
        },
        "title": {
          "metadata": {
            "description": "Video title."
          },
          "type": "string"
        },
        "options": {
          "ref": "PlaybackOptions",
          "metadata": {
            "description": "Playback details."
          }
        }
      },
      "metadata": {
        "section": "Video"
      }
    },
    "PlaybackOptions": {
      "optionalProperties": {
        "autoPlay": {
          "metadata": {
            "description": "Whether the media automatically starts playing after opening a page."
          },
          "type": "boolean"
        },
        "playInLoop": {
          "type": "boolean",
          "metadata": {
            "description": "Whether the media is looped."
          }
        },
        "showControls": {
          "type": "boolean",
          "metadata": {
            "description": "Whether to show the media controls."
          }
        }
      },
      "metadata": {
        "section": "Video"
      }
    },
    "RootNode": {
      "discriminator": "type",
      "metadata": {
        "section": "RootNode",
        "description": "Nodes that can be added to the root level of `nodes` array.",
        "refUnion": {
          "PARAGRAPH": "ParagraphNode",
          "HEADING": "HeadingNode",
          "BULLETED_LIST": "BulletedListNode",
          "ORDERED_LIST": "OrderedListNode",
          "BLOCKQUOTE": "BlockquoteNode",
          "CODE_BLOCK": "CodeBlockNode",
          "VIDEO": "VideoNode",
          "DIVIDER": "DividerNode",
          "FILE": "FileNode",
          "GALLERY": "GalleryNode",
          "GIF": "GifNode",
          "HTML": "HtmlNode",
          "IMAGE": "ImageNode",
          "LINK_PREVIEW": "LinkPreviewNode",
          "POLL": "PollNode",
          "APP_EMBED": "AppEmbedNode",
          "BUTTON": "ButtonNode",
          "COLLAPSIBLE_LIST": "CollapsibleListNode",
          "TABLE": "TableNode",
          "EMBED": "EmbedNode",
          "AUDIO": "AudioNode",
          "LAYOUT": "LayoutNode"
        }
      },
      "mapping": {
        "PARAGRAPH": {
          "metadata": {
            "section": "Paragraph",
            "description": "Structures text content, providing a clear separation between different ideas or sections of text. Paragraphs are rendered with default styling that typically includes some space above and below the paragraph, making it visually distinct from the surrounding content.",
            "examples": [
              "paragraph"
            ]
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Paragraph children."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Vertical padding. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "paragraphData": {
              "ref": "ParagraphData",
              "metadata": {
                "description": "Paragraph node attributes."
              }
            }
          }
        },
        "HEADING": {
          "metadata": {
            "section": "Heading",
            "examples": [
              "headings-all-sizes"
            ],
            "description": "Indicates a heading or title of a section. There are 6 levels of headings, with `1` being the most prominent and `6` being the least."
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Child nodes to define the heading text."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Heading styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            },
            "headingData": {
              "ref": "HeadingData",
              "metadata": {
                "description": "Heading options."
              }
            }
          }
        },
        "BULLETED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "bulletedList"
            ],
            "description": "Bulleted list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "bulletedListData": {
              "ref": "BulletedListData",
              "metadata": {
                "description": "Bulleted list details."
              }
            }
          }
        },
        "ORDERED_LIST": {
          "metadata": {
            "section": "List",
            "examples": [
              "orderedList"
            ],
            "description": "Numbered list."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ListItemNode",
                "metadata": {
                  "validation": {
                    "minSize": 1
                  }
                }
              },
              "metadata": {
                "description": "List items.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "orderedListData": {
              "ref": "OrderedListData",
              "metadata": {
                "description": "Ordered list details."
              }
            }
          }
        },
        "BLOCKQUOTE": {
          "metadata": {
            "section": "Block quote",
            "examples": [
              "blockquote"
            ],
            "description": "Used to visually distinguish quoted or cited content from the surrounding text. When rendered, it displays as the quoted text with indentation or other styling to visually separate it from the surrounding content."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "ParagraphNode"
              },
              "metadata": {
                "description": "Child nodes to define a blockquote paragraph.",
                "validation": {
                  "minSize": 1,
                  "maxSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "blockquoteData": {
              "ref": "BlockquoteData",
              "metadata": {
                "description": "Blockquote details."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Defines the blockquote style, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            }
          }
        },
        "CODE_BLOCK": {
          "metadata": {
            "section": "Codeblock",
            "features": [
              "CODE_BLOCK"
            ],
            "examples": [
              "code-block-multi-line",
              "code-block-syntax-highlighting"
            ],
            "description": "Presents code snippets, markup, or other text-based content that should be displayed exactly as it appears, without any interpretation or formatting applied by the browser. When rendered, it is displayed as the code block with a monospaced font, preserving indentation, line breaks, and spacing as specified in the code."
          },
          "properties": {},
          "optionalProperties": {
            "nodes": {
              "elements": {
                "ref": "TextNode"
              },
              "metadata": {
                "description": "Child nodes to define a code block text."
              }
            },
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "codeBlockData": {
              "ref": "CodeBlockData",
              "metadata": {
                "description": "Code block details."
              }
            },
            "style": {
              "ref": "NodeStyle",
              "metadata": {
                "description": "Code block styling, such as padding and background color. \n **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled."
              }
            }
          }
        },
        "VIDEO": {
          "metadata": {
            "section": "Video",
            "examples": [
              "video-url",
              "video-wix"
            ],
            "features": [
              "VIDEO"
            ],
            "description": "Embeds and plays video content directly in a page. When rendered, it is displayed as the video player with the specified video file. Users can interact with the controls to play, pause, rewind, and adjust the volume of the video."
          },
          "properties": {
            "videoData": {
              "ref": "VideoData",
              "metadata": {
                "description": "Video details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define a video caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "DIVIDER": {
          "metadata": {
            "section": "Divider",
            "examples": [
              "divider"
            ],
            "features": [
              "DIVIDER"
            ],
            "description": "Horizontal line between different content."
          },
          "properties": {},
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "dividerData": {
              "ref": "DividerData",
              "metadata": {
                "description": "Divider details."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "FILE": {
          "metadata": {
            "section": "File",
            "examples": [
              "file"
            ],
            "features": [
              "FILE"
            ],
            "description": "File that is uploaded and accessed in a page. Files can be different types of content, such as text documents, images, and videos."
          },
          "properties": {
            "fileData": {
              "ref": "FileData",
              "metadata": {
                "description": "File details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "GALLERY": {
          "metadata": {
            "section": "Gallery",
            "examples": [
              "gallery"
            ],
            "features": [
              "GALLERY"
            ],
            "description": "Collection of images displayed together."
          },
          "properties": {
            "galleryData": {
              "ref": "GalleryData",
              "metadata": {
                "description": "Gallery node details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "GIF": {
          "metadata": {
            "section": "Gif",
            "examples": [
              "gif-basic"
            ],
            "features": [
              "GIF"
            ],
            "description": "GIF images."
          },
          "properties": {
            "gifData": {
              "ref": "GIFData",
              "metadata": {
                "description": "GIF details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define a GIF caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "HTML": {
          "metadata": {
            "section": "Html",
            "examples": [
              "html-code",
              "html-url"
            ],
            "features": [
              "HTML"
            ],
            "description": "HTML block that incorporates external content, such as interactive maps or entire web pages."
          },
          "properties": {
            "htmlData": {
              "ref": "HTMLData",
              "metadata": {
                "description": "HTML details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "IMAGE": {
          "metadata": {
            "section": "Image",
            "examples": [
              "image-id",
              "image-url"
            ],
            "features": [
              "IMAGE"
            ],
            "description": "Standalone images in a page."
          },
          "properties": {
            "imageData": {
              "ref": "ImageData",
              "metadata": {
                "description": "Image options."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "description": "Child nodes to define an image caption."
              },
              "elements": {
                "ref": "CaptionNode"
              }
            }
          }
        },
        "LINK_PREVIEW": {
          "metadata": {
            "section": "Link Preview",
            "examples": [
              "link-preview"
            ],
            "features": [
              "LINK_PREVIEW"
            ],
            "description": "Preview or summary of a webpage. Link previews include elements such as the title of the linked page, a brief description or excerpt of the content, and sometimes a thumbnail image or other relevant information."
          },
          "properties": {
            "linkPreviewData": {
              "ref": "LinkPreviewData",
              "metadata": {
                "description": "Link preview details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "POLL": {
          "metadata": {
            "section": "Poll",
            "examples": [
              "poll"
            ],
            "features": [
              "POLL"
            ],
            "description": "Provides a question with several options, where the options' cumulative total may be shown."
          },
          "properties": {
            "pollData": {
              "ref": "PollData",
              "metadata": {
                "description": "Poll details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "APP_EMBED": {
          "metadata": {
            "section": "App Embed",
            "examples": [
              "app-embed-event",
              "app-embed-booking"
            ],
            "features": [
              "APP_EMBED"
            ],
            "description": "Embeds an external content preview from Wix business solutions. For example, you can embed previews from the Wix Bookings, Wix Stores and Wix Events apps."
          },
          "properties": {
            "appEmbedData": {
              "ref": "AppEmbedData",
              "metadata": {
                "description": "Embedded Wix app."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "BUTTON": {
          "metadata": {
            "section": "Button",
            "examples": [
              "button-link",
              "button-action",
              "button-styled"
            ],
            "features": [
              "BUTTON"
            ],
            "description": "Button to trigger an action."
          },
          "properties": {
            "buttonData": {
              "ref": "ButtonData",
              "metadata": {
                "description": "Button details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "COLLAPSIBLE_LIST": {
          "metadata": {
            "section": "Collapsible List",
            "examples": [
              "collapsible-list"
            ],
            "features": [
              "COLLAPSIBLE_LIST"
            ],
            "description": "List of items where each item can be expanded or collapsed."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "CollapsibleItemNode"
              },
              "metadata": {
                "description": "Collapsible list child nodes.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "collapsibleListData": {
              "ref": "CollapsibleListData",
              "metadata": {
                "description": "Collapsible list details."
              }
            }
          }
        },
        "TABLE": {
          "metadata": {
            "section": "Table",
            "examples": [
              "table"
            ],
            "features": [
              "TABLE"
            ],
            "description": "Data in rows and columns."
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "TableRowNode"
              },
              "metadata": {
                "description": "Child nodes to define table rows.",
                "validation": {
                  "minSize": 1
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "tableData": {
              "ref": "TableData",
              "metadata": {
                "description": "Table details."
              }
            }
          }
        },
        "EMBED": {
          "metadata": {
            "section": "Embed",
            "examples": [
              "embed"
            ],
            "features": [
              "EMBED"
            ],
            "description": "Embedded content when you include a link to a resource, without having to parse the resource directly. This provides a standardized way for web developers to include content such as videos, images, and other multimedia elements from supported providers. To learn more, see [oembed](https://oembed.com/)."
          },
          "properties": {
            "embedData": {
              "ref": "EmbedData",
              "metadata": {
                "description": "oEmbed details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "AUDIO": {
          "metadata": {
            "section": "Audio",
            "examples": [
              "audio-soundcloud"
            ],
            "features": [
              "AUDIO"
            ],
            "description": "Embeds and plays audio content directly in a webpage. Users can interact with the controls to play, pause, rewind, and adjust the volume of the audio."
          },
          "properties": {
            "audioData": {
              "ref": "AudioData",
              "metadata": {
                "description": "Audio details."
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "nodes": {
              "metadata": {
                "internal": true
              },
              "elements": {
                "ref": "Never"
              }
            }
          }
        },
        "LAYOUT": {
          "metadata": {
            "section": "Layout",
            "examples": [
              "layout"
            ],
            "description": "Used to wrap cells in layout container. When rendered, it cells one after the other in a row"
          },
          "properties": {
            "nodes": {
              "elements": {
                "ref": "LayoutCellNode"
              },
              "metadata": {
                "description": "Cell items.",
                "validation": {
                  "minSize": 1,
                  "maxSize": 3
                }
              }
            }
          },
          "optionalProperties": {
            "id": {
              "type": "string",
              "metadata": {
                "format": "NODE_ID",
                "description": "[Node ID](#node-ids)."
              }
            },
            "layoutData": {
              "ref": "LayoutData",
              "metadata": {
                "description": "Layout details."
              }
            }
          }
        }
      }
    }
  }
}